import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { fetchSceneElements, updateArtworkPosition, addWall, updateWall, deleteWall, updateWallOrder, updateCollectionArtworkMetadata, updateArtworkDimensions, getFloorData, checkArtworksWithAudio, searchArtistArtwork, addArtworkToCollection, removeArtworkFromCollection, getArtistArtworkByUuid, getPreviousGalleryCollections, copyWallsFromCollection, getCollectionInventoryArtworks, createAndPlaceArtwork, addPod, addEasel, addStructure, updateStructure, updateSceneElement, deleteSceneElement, syncArtworkPositionsOnWall } from '../utils/api';
import WallComponent from './WallComponent';
import InventoryItem from './InventoryItem';
import WallModal from './WallModal';
import Toast from './Toast';
import TopDownWallPlanner from './TopDownWallPlanner';
import { supabase } from '../utils/supabaseClient';

import { getCloudinaryUrl } from '../utils/imageUtils';
import { ChevronDown, Palette, Layers, Zap, RotateCcw, RefreshCw, Printer, Copy } from 'lucide-react';
import { jsPDF } from 'jspdf';
import '../styles/GalleryWallPlanner.css';

const GalleryWallPlanner = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const [sceneData, setSceneData] = useState({ walls: [], unplaced: [], easels: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentDraggedItem, setCurrentDraggedItem] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [statusMsg, setStatusMsg] = useState(null); // { message, type, id }
  const statusTimerRef = useRef(null);
  const [showWallModal, setShowWallModal] = useState(false);
  const [editingWallId, setEditingWallId] = useState(null);
  const [collectionInfo, setCollectionInfo] = useState(null);
  const [isUpdatingMetadata, setIsUpdatingMetadata] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showTopDownPlanner, setShowTopDownPlanner] = useState(searchParams.get('view') === 'topdown');
  const [floorData, setFloorData] = useState(null);
  const [artworkAudioMap, setArtworkAudioMap] = useState({});
  const [showCuratorTools, setShowCuratorTools] = useState(false);
  const curatorToolsRef = useRef(null);
  
  // Copy walls from previous collection state
  const [showCopyWallsModal, setShowCopyWallsModal] = useState(false);
  const [previousCollections, setPreviousCollections] = useState([]);
  const [galleryInfo, setGalleryInfo] = useState(null);
  const [isCopyingWalls, setIsCopyingWalls] = useState(false);
  const [isLoadingPreviousCollections, setIsLoadingPreviousCollections] = useState(false);
  
  // Inventory state with tabs for accepted/pending
  const [inventoryTab, setInventoryTab] = useState('accepted'); // 'accepted' or 'pending'
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryData, setInventoryData] = useState({ accepted: [], pending: [], totalAccepted: 0, totalPending: 0, totalPlaced: 0 });
  const [inventoryDisplayCount, setInventoryDisplayCount] = useState(30); // For infinite scroll
  const inventoryContainerRef = useRef(null);
  
  // Artist search state (reverse workflow)
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [artistSearchResults, setArtistSearchResults] = useState(null);
  const [isSearchingArtist, setIsSearchingArtist] = useState(false);
  const [showArtistSearch, setShowArtistSearch] = useState(false);
  const [addingArtworkId, setAddingArtworkId] = useState(null);
  const [artworkFilterQuery, setArtworkFilterQuery] = useState(''); // Filter within artist's artwork
  
  // Dimension edit modal state
  const [showDimensionModal, setShowDimensionModal] = useState(false);
  const [editingArtworkDimensions, setEditingArtworkDimensions] = useState(null);
  const [dimensionFormData, setDimensionFormData] = useState({ width: '', height: '', hanger_height: '' });
  const [isSavingDimensions, setIsSavingDimensions] = useState(false);
  
  // Measurements modal state (shows placement info + inline hanger height edit)
  const [showMeasurementsModal, setShowMeasurementsModal] = useState(false);
  const [measurementsArtwork, setMeasurementsArtwork] = useState(null); // { artwork, wallWidth, wallHeight }
  const [measurementsHangerHeight, setMeasurementsHangerHeight] = useState('');
  const [isSavingMeasurements, setIsSavingMeasurements] = useState(false);
  
  // Close curator tools dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (curatorToolsRef.current && !curatorToolsRef.current.contains(e.target)) {
        setShowCuratorTools(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Load last artist search from localStorage on mount
  useEffect(() => {
    try {
      const savedSearch = localStorage.getItem('galleryWallPlanner_lastArtistSearch');
      if (savedSearch) {
        const parsed = JSON.parse(savedSearch);
        if (parsed.query) setArtistSearchQuery(parsed.query);
        if (parsed.results) setArtistSearchResults(parsed.results);
        if (parsed.showSearch) setShowArtistSearch(parsed.showSearch);
      }
    } catch (error) {
      console.warn('Error loading saved artist search:', error);
    }
  }, []);
  
  // Save artist search to localStorage when it changes
  useEffect(() => {
    if (artistSearchResults || artistSearchQuery) {
      try {
        localStorage.setItem('galleryWallPlanner_lastArtistSearch', JSON.stringify({
          query: artistSearchQuery,
          results: artistSearchResults,
          showSearch: showArtistSearch,
          savedAt: new Date().toISOString()
        }));
      } catch (error) {
        console.warn('Error saving artist search:', error);
      }
    }
  }, [artistSearchQuery, artistSearchResults, showArtistSearch]);

  
  // Constants for scaling
  const scale = 2; // 1 inch = 2 pixels
  
  // Refs
  const wallsContainerRef = useRef(null);
  
  // Get collection ID from URL params - check both query params and route params
  const rawCollectionId = searchParams.get('collection_id') || params.collectionId || null;
  
  // Convert collectionId to integer and validate
  const collectionId = rawCollectionId ? parseInt(rawCollectionId, 10) : null;
  
  // Redirect if no collection ID is provided
  useEffect(() => {
    if (!collectionId || isNaN(collectionId)) {
      console.warn('Invalid collection ID provided, redirecting to home page');
      navigate('/');
    }
  }, [collectionId, navigate]);
  
  // Load scene data function
  const loadData = async () => {
    if (!collectionId || isNaN(collectionId)) {
      console.warn('Invalid collectionId provided to loadData:', collectionId);
      return;
    }
    
    try {
      setIsLoading(true);
      
      console.log(`Fetching data for collection ${collectionId}`);
      
      // Fetch the collection data
      const responseData = await fetchSceneElements(collectionId, '2d');
      console.log("Raw API Response:", responseData);
      console.log("Walls found:", responseData.walls?.length || 0);
      console.log("Unplaced artwork found:", responseData.unplaced?.length || 0);
      
      // Debug wall data
      if (responseData.walls && responseData.walls.length > 0) {
        console.log("Sample wall:", responseData.walls[0]);
        console.log("Wall artwork counts:", responseData.walls.map(w => ({
          wallId: w.element_id,
          wallName: w.source,
          artworkCount: w.artwork?.length || 0
        })));
      }
      
      // Debug unplaced artwork
      if (responseData.unplaced && responseData.unplaced.length > 0) {
        console.log("Sample unplaced artwork:", responseData.unplaced[0]);
      }
      
      // Initialize empty arrays if needed
      if (!responseData.walls) {
        console.log('No walls found in response, initializing empty array');
        responseData.walls = [];
      }
      
      if (!responseData.unplaced) {
        console.log('No unplaced artwork found in response, initializing empty array');
        responseData.unplaced = [];
      }
      
      setSceneData(responseData);
      
      // Check for audio stories in artworks
      try {
        const allVcIds = [
          ...responseData.walls.flatMap(wall => wall.artwork?.map(art => art.vc_id) || []),
          ...responseData.unplaced?.map(art => art.vc_id) || []
        ];
        
        if (allVcIds.length > 0) {
          const audioMap = await checkArtworksWithAudio(collectionId, allVcIds);
          setArtworkAudioMap(audioMap);
          console.log('Audio mapping loaded:', Object.keys(audioMap).filter(k => audioMap[k]).length, 'artworks with audio');
        }
      } catch (audioError) {
        console.warn('Error checking artwork audio stories:', audioError);
        setArtworkAudioMap({});
      }
      
      // Load floor data
      try {
        const floorData = await getFloorData(collectionId);
        if (floorData) {
          setFloorData(floorData);
          console.log('Floor data loaded:', floorData);
        }
      } catch (floorError) {
        console.warn('Error loading floor data:', floorError);
      }
      
      // Fetch collection info
      try {
        const { fetchCollectionDetails } = await import('../utils/api');
        const collectionData = await fetchCollectionDetails(collectionId);
        setCollectionInfo(collectionData);
      } catch (collectionError) {
        console.error('Error fetching collection details:', collectionError);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setError(error.message || 'Failed to fetch scene elements');
      setIsLoading(false);
      
      // Create default data structure if loading fails
      setSceneData({
        walls: [],
        unplaced: []
      });
      
      showToast('Error loading gallery data. You can still create walls and add artwork manually.', 'error');
    }
  };
  
  // Load inventory artworks from collection_vc_mapping
  const loadInventoryData = async () => {
    if (!collectionId || isNaN(collectionId)) return;
    
    try {
      const data = await getCollectionInventoryArtworks(collectionId);
      setInventoryData(data);
      console.log('📦 Inventory loaded:', data.totalAccepted, 'accepted,', data.totalPending, 'pending');
    } catch (error) {
      console.error('Error loading inventory data:', error);
    }
  };
  
  // Load scene data
  useEffect(() => {
    loadData();
  }, [collectionId]);
  
  // Load inventory data
  useEffect(() => {
    loadInventoryData();
  }, [collectionId]);
  
  // Handle inventory scroll for infinite loading
  const handleInventoryScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      // Near bottom, load more
      setInventoryDisplayCount(prev => prev + 30);
    }
  };
  
  // Show toast message — single auto-replacing status bar (no stacking)
  const showToast = (message, type = 'info') => {
    const id = Date.now();
    setStatusMsg({ message, type, id });
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    statusTimerRef.current = setTimeout(() => {
      setStatusMsg(prev => prev?.id === id ? null : prev);
    }, 2500);
  };
  
  // Handle drag start
  const handleDragStart = (e, artwork) => {
    console.log('🎨 Drag started for artwork:', artwork.element_id, artwork.vc_id);
    setCurrentDraggedItem(artwork);
    
    // Create a smaller ghost image for dragging
    if (artwork.source) {
      const ghostImg = new Image();
      ghostImg.src = getOptimizedImageUrl(artwork.source, 'drag');
      
      // Set a smaller size for the drag image
      ghostImg.width = 100;
      ghostImg.height = 100 * (artwork.height / artwork.width);
      
      // Wait a tiny bit for the image to load before setting it
      setTimeout(() => {
        e.dataTransfer.setDragImage(ghostImg, 50, 50);
      }, 10);
    }
    
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', artwork.element_id);
  };
  
  // Handle drop on wall
  const handleDrop = async (e, wallId) => {
    e.preventDefault();
    
    if (!currentDraggedItem) return;
    
    console.log('🎨 Drop on wall:', wallId, 'for artwork:', currentDraggedItem.element_id, 'vc_id:', currentDraggedItem.vc_id);
    
    const rect = e.currentTarget.getBoundingClientRect();
    const wallEl = document.getElementById(`wall-${wallId}`);
    
    // Get the wall's specific scale factors
    const xScale = parseFloat(wallEl.dataset.scaleX) || scale;
    const yScale = parseFloat(wallEl.dataset.scaleY) || scale;
    
    // Calculate position in wall coordinates
    const x = (e.clientX - rect.left) / xScale;
    const y = (e.clientY - rect.top) / yScale;
    
    // Adjust position to center the artwork on the drop point
    const posX = Math.max(0, Math.min(x - (currentDraggedItem.width / 2), wallEl.offsetWidth / xScale - currentDraggedItem.width));
    const posY = Math.max(0, Math.min(y - (currentDraggedItem.height / 2), wallEl.offsetHeight / yScale - currentDraggedItem.height));
    
    // Round to 2 decimal places for more precise positioning
    const finalPosX = Math.round(posX * 100) / 100;
    const finalPosY = Math.round(posY * 100) / 100;
    
    console.log('🎨 Calculated position:', { x, y, posX, posY, scale: { xScale, yScale } });
    
    try {
      // Check if this is from the new inventory (element_id equals vc_id means it's from collection_vc_mapping)
      const isFromNewInventory = currentDraggedItem.isFromInventory || 
        (currentDraggedItem.element_id === currentDraggedItem.vc_id && !currentDraggedItem.isInSceneElements);
      
      if (isFromNewInventory) {
        // Create scene_element and place on wall in one operation
        console.log('🎨 Creating scene_element for inventory artwork:', currentDraggedItem.vc_id);
        const result = await createAndPlaceArtwork(
          collectionId,
          currentDraggedItem.vc_id,
          wallId,
          finalPosX,
          finalPosY,
          currentDraggedItem
        );
        
        if (result.success) {
          // Build the artwork object from the returned element
          const newArtwork = {
            element_id: result.element.element_id,
            vc_id: result.element.vc_id,
            source: result.element.source || currentDraggedItem.source,
            width: result.element.width || currentDraggedItem.width,
            height: result.element.height || currentDraggedItem.height,
            wall_position_x: finalPosX,
            wall_position_y: finalPosY,
            parent_element_id: wallId,
            title: currentDraggedItem.title,
            artist_name: currentDraggedItem.artist_name,
            ...result.element
          };
          
          // Update local state - add artwork to the target wall
          setSceneData(prev => ({
            ...prev,
            walls: prev.walls.map(wall => {
              if (wall.element_id === wallId) {
                return {
                  ...wall,
                  artwork: [...(wall.artwork || []), newArtwork]
                };
              }
              return wall;
            }),
            // Remove from unplaced if it was there
            unplaced: prev.unplaced?.filter(a => a.vc_id !== currentDraggedItem.vc_id) || []
          }));
          
          // Refresh inventory to remove placed artwork
          loadInventoryData();
          
          showToast('Artwork placed on wall', 'success');
        }
      } else {
        // Existing artwork - just update position
        await updateArtworkPosition(currentDraggedItem.element_id, wallId, finalPosX, finalPosY);
        
        // Update local state
        updateLocalArtworkPosition(currentDraggedItem.element_id, wallId, finalPosX, finalPosY);
        
        showToast('Artwork position updated', 'success');
      }
    } catch (error) {
      console.error('Error updating artwork position:', error);
      showToast('Failed to update artwork position', 'error');
    }
    
    // Reset drag state
    setCurrentDraggedItem(null);
  };

  // Handle repositioning artwork that's already on a wall
  const handleArtworkReposition = async (e, artworkId, wallId) => {
    e.preventDefault();
    
    const rect = e.currentTarget.getBoundingClientRect();
    const wallEl = document.getElementById(`wall-${wallId}`);
    
    // Get the wall's specific scale factors
    const xScale = parseFloat(wallEl.dataset.scaleX) || scale;
    const yScale = parseFloat(wallEl.dataset.scaleY) || scale;
    
    // Calculate position in wall coordinates
    const x = (e.clientX - rect.left) / xScale;
    const y = (e.clientY - rect.top) / yScale;
    
    // Find the artwork being repositioned - search more broadly
    let artwork = null;
    
    // First try to find it on the current wall
    const currentWall = sceneData.walls.find(w => w.element_id === wallId);
    if (currentWall && currentWall.artwork) {
      artwork = currentWall.artwork.find(a => a.element_id.toString() === artworkId.toString());
    }
    
    // If not found, search all walls
    if (!artwork) {
      for (const wall of sceneData.walls) {
        if (wall.artwork) {
          artwork = wall.artwork.find(a => a.element_id.toString() === artworkId.toString());
          if (artwork) {
            console.log('🎨 Found artwork on different wall:', wall.element_id, wall.source);
            break;
          }
        }
      }
    }
    
    // If still not found, check unplaced
    if (!artwork && sceneData.unplaced) {
      artwork = sceneData.unplaced.find(a => a.element_id.toString() === artworkId.toString());
      if (artwork) {
        console.log('🎨 Found artwork in unplaced');
      }
    }
    
    if (!artwork) {
      console.error('❌ Artwork not found for repositioning:', artworkId);
      console.log('🔍 Available artwork IDs:', [
        ...sceneData.walls.flatMap(w => w.artwork?.map(a => a.element_id) || []),
        ...sceneData.unplaced?.map(a => a.element_id) || []
      ]);
      console.log('🔍 Looking for artworkId type:', typeof artworkId, 'value:', artworkId);
      return;
    }
    
    // Adjust position to center the artwork on the drop point
    const posX = Math.max(0, Math.min(x - (artwork.width / 2), wallEl.offsetWidth / xScale - artwork.width));
    const posY = Math.max(0, Math.min(y - (artwork.height / 2), wallEl.offsetHeight / yScale - artwork.height));
    
    // Round to 2 decimal places for more precise positioning
    const finalPosX = Math.round(posX * 100) / 100;
    const finalPosY = Math.round(posY * 100) / 100;
    
    try {
      // Update artwork position
      await updateArtworkPosition(artworkId, wallId, finalPosX, finalPosY);
      
      // Update local state
      updateLocalArtworkPosition(artworkId, wallId, finalPosX, finalPosY);
      
      showToast('Artwork repositioned', 'success');
    } catch (error) {
      console.error('Error repositioning artwork:', error);
      showToast('Failed to reposition artwork', 'error');
    }
  };
  
  // Update local artwork position state
  const updateLocalArtworkPosition = (artworkId, wallId, posX, posY) => {
    console.log('🔄 Updating local artwork position:', { artworkId, wallId, posX, posY });
    
    // Clone scene data for updating
    const updatedSceneData = { ...sceneData };
    
    // Find the artwork in our data
    let artwork = null;
    let sourceArray = null;
    let sourceIndex = -1;
    
    // Check in walls first
    for (const wall of updatedSceneData.walls) {
      if (wall.artwork) {
        const foundIndex = wall.artwork.findIndex(art => art.element_id.toString() === artworkId.toString());
        if (foundIndex > -1) {
          artwork = wall.artwork[foundIndex];
          sourceArray = wall.artwork;
          sourceIndex = foundIndex;
          console.log('🎨 Found artwork in wall:', wall.element_id, wall.source);
          break;
        }
      }
    }
    
    // If not found in walls, check unplaced
    if (!artwork && updatedSceneData.unplaced) {
      const foundIndex = updatedSceneData.unplaced.findIndex(art => art.element_id.toString() === artworkId.toString());
      if (foundIndex > -1) {
        artwork = updatedSceneData.unplaced[foundIndex];
        sourceArray = updatedSceneData.unplaced;
        sourceIndex = foundIndex;
        console.log('🎨 Found artwork in unplaced');
      }
    }
    
    if (!artwork) {
      console.error('❌ Artwork not found:', artworkId);
      console.log('🔍 Available artwork IDs:', [
        ...updatedSceneData.walls.flatMap(w => w.artwork?.map(a => a.element_id) || []),
        ...updatedSceneData.unplaced?.map(a => a.element_id) || []
      ]);
      console.log('🔍 Looking for artworkId type:', typeof artworkId, 'value:', artworkId);
      console.log('🔍 Wall artwork counts:', updatedSceneData.walls.map(w => ({
        wallId: w.element_id,
        wallName: w.source,
        artworkCount: w.artwork?.length || 0,
        artworkIds: w.artwork?.map(a => a.element_id) || []
      })));
      return;
    }
    
    console.log('🎨 Moving artwork:', artwork.vc_id || artwork.element_id, 'from', sourceArray === updatedSceneData.unplaced ? 'unplaced' : 'wall', 'to wall:', wallId);
    
    // Remove from current location
    if (sourceArray && sourceIndex > -1) {
      sourceArray.splice(sourceIndex, 1);
    }
    
    // Update position and wall
    artwork.wall_position_x = posX;
    artwork.wall_position_y = posY;
    artwork.parent_element_id = wallId;
    
    // Add to new wall
    const targetWall = updatedSceneData.walls.find(w => w.element_id === wallId);
    if (targetWall) {
      if (!targetWall.artwork) {
        targetWall.artwork = [];
      }
      targetWall.artwork.push(artwork);
      console.log('✅ Added artwork to wall:', targetWall.source, 'Total artwork on wall:', targetWall.artwork.length);
    } else {
      console.error('❌ Target wall not found:', wallId);
    }
    
    // Update state
    setSceneData(updatedSceneData);
  };
  
  // Handle nudge artwork (precise positioning with arrow keys or buttons)
  const handleNudgeArtwork = async (artworkId, wallId, dx, dy) => {
    console.log('🔄 Nudging artwork:', { artworkId, wallId, dx, dy });
    
    // Find the wall and artwork
    const wall = sceneData.walls.find(w => w.element_id === wallId);
    if (!wall || !wall.artwork) {
      console.error('Wall not found:', wallId);
      return;
    }
    
    const artwork = wall.artwork.find(a => a.element_id.toString() === artworkId.toString());
    if (!artwork) {
      console.error('Artwork not found on wall:', artworkId);
      return;
    }
    
    // Get current position
    const currentX = artwork.wall_position_x !== undefined ? artwork.wall_position_x : 
                    (artwork.position_x !== undefined ? artwork.position_x : 0);
    const currentY = artwork.wall_position_y !== undefined ? artwork.wall_position_y : 
                    (artwork.position_y !== undefined ? artwork.position_y : 0);
    
    // Calculate new position with bounds checking
    const newX = Math.max(0, Math.min(currentX + dx, wall.width - artwork.width));
    const newY = Math.max(0, Math.min(currentY + dy, wall.height - artwork.height));
    
    // Round to 2 decimal places
    const finalX = Math.round(newX * 100) / 100;
    const finalY = Math.round(newY * 100) / 100;
    
    // Skip if no change
    if (finalX === currentX && finalY === currentY) {
      return;
    }
    
    try {
      // Update in database
      await updateArtworkPosition(artworkId, wallId, finalX, finalY);
      
      // Update local state
      setSceneData(prevData => {
        const updatedWalls = prevData.walls.map(w => {
          if (w.element_id === wallId) {
            return {
              ...w,
              artwork: w.artwork.map(a => {
                if (a.element_id.toString() === artworkId.toString()) {
                  return {
                    ...a,
                    wall_position_x: finalX,
                    wall_position_y: finalY
                  };
                }
                return a;
              })
            };
          }
          return w;
        });
        return { ...prevData, walls: updatedWalls };
      });
      
    } catch (error) {
      console.error('Error nudging artwork:', error);
      showToast('Failed to move artwork', 'error');
    }
  };
  
  // Remove artwork from wall
  const removeArtwork = async (artworkId) => {
    try {
      // Clone scene data for updating
      const updatedSceneData = { ...sceneData };
      
      // Find the artwork
      let artwork = null;
      let parentWall = null;
      
      for (const wall of updatedSceneData.walls) {
        if (wall.artwork) {
          const index = wall.artwork.findIndex(art => art.element_id === artworkId);
          if (index > -1) {
            artwork = wall.artwork[index];
            parentWall = wall;
            wall.artwork.splice(index, 1);
            break;
          }
        }
      }
      
      if (!artwork) {
        console.error('Artwork not found:', artworkId);
        return;
      }
      
      // Clear parent reference
      artwork.parent_element_id = null;
      
      // Add to unplaced
      if (!updatedSceneData.unplaced) {
        updatedSceneData.unplaced = [];
      }
      updatedSceneData.unplaced.push(artwork);
      
      // Update state
      setSceneData(updatedSceneData);
      
      // Update in database
      await updateArtworkPosition(artworkId, null, 
        artwork.wall_position_x || artwork.position_x || 0, 
        artwork.wall_position_y || artwork.position_y || 0);
      
      showToast('Artwork removed from wall', 'success');
    } catch (error) {
      console.error('Error removing artwork:', error);
      showToast('Failed to remove artwork from wall', 'error');
    }
  };
  
  // Handle adding or editing a wall
  const handleWallSubmit = async (formData) => {
    try {
      const { name, width, height } = formData;
      
      if (editingWallId) {
        // Updating an existing wall
        await updateWall(editingWallId, name, width, height);
        
        // Update local state
        const updatedWalls = sceneData.walls.map(wall => {
          if (wall.element_id === editingWallId) {
            return {
              ...wall,
              source: name,
              width: parseFloat(width),
              height: parseFloat(height)
            };
          }
          return wall;
        });
        
        setSceneData({ ...sceneData, walls: updatedWalls });
        showToast('Wall updated successfully', 'success');
      } else {
        // Adding a new wall
        const newWall = await addWall(collectionId, name, width, height);
        
        // Update local state with new wall
        setSceneData(prevData => ({ 
          ...prevData,
          walls: [...prevData.walls, {
            ...newWall.wall,
            artwork: []
          }]
        }));
        
        showToast('Wall added successfully', 'success');
      }
      
      // Reset editing state and close modal
      setEditingWallId(null);
      setShowWallModal(false);
    } catch (error) {
      console.error('Error saving wall:', error);
      showToast('Failed to save wall', 'error');
    }
  };
  
  // Open wall edit modal
  const editWall = (wallId) => {
    console.log('🔧 Edit wall clicked:', wallId);
    const wall = sceneData.walls.find(w => w.element_id === wallId);
    if (wall) {
      console.log('🔧 Found wall to edit:', wall);
      setEditingWallId(wallId);
      setShowWallModal(true);
      console.log('🔧 Set modal state to true');
    } else {
      console.error('❌ Wall not found for editing:', wallId);
    }
  };
  
  // Delete a wall
  const handleDeleteWall = async (wallId) => {
    if (!window.confirm('Are you sure you want to delete this wall? All artwork on this wall will be moved to your inventory.')) {
      return;
    }
    
    try {
      // Find the wall
      const wall = sceneData.walls.find(w => w.element_id === wallId);
      if (!wall) return;
      
      // Move all artwork to unplaced
      const updatedSceneData = { ...sceneData };
      
      if (wall.artwork && wall.artwork.length > 0) {
        if (!updatedSceneData.unplaced) {
          updatedSceneData.unplaced = [];
        }
        
        // Move each artwork to unplaced and update parent
        for (const art of wall.artwork) {
          art.parent_element_id = null;
          updatedSceneData.unplaced.push(art);
          
          // Update in database
          await updateArtworkPosition(art.element_id, null, 
            art.wall_position_x || art.position_x || 0, 
            art.wall_position_y || art.position_y || 0);
        }
      }
      
      // Remove wall from scene data
      updatedSceneData.walls = updatedSceneData.walls.filter(w => w.element_id !== wallId);
      
      // Update state
      setSceneData(updatedSceneData);
      
      // Delete wall from database
      await deleteWall(wallId);
      
      showToast('Wall deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting wall:', error);
      showToast('Failed to delete wall', 'error');
    }
  };
  
  // Delete easel
  const handleDeleteEasel = async (easelId) => {
    if (!window.confirm('Delete this easel? Any artwork on it will be moved to inventory.')) return;
    try {
      const easel = (sceneData.easels || []).find(e => e.element_id === easelId);
      if (easel?.artwork?.length) {
        for (const art of easel.artwork) {
          await updateArtworkPosition(art.element_id, null, 0, 0);
        }
      }
      await deleteSceneElement(easelId);
      setSceneData(prev => ({ ...prev, easels: (prev.easels || []).filter(e => e.element_id !== easelId) }));
      showToast('Easel deleted', 'success');
    } catch (err) {
      console.error('Error deleting easel:', err);
      showToast('Failed to delete easel', 'error');
    }
  };

  // Delete structure
  const handleDeleteStructure = async (structureId) => {
    if (!window.confirm('Delete this structure?')) return;
    try {
      await deleteSceneElement(structureId);
      setSceneData(prev => ({ ...prev, structures: (prev.structures || []).filter(s => s.element_id !== structureId) }));
      showToast('Structure deleted', 'success');
    } catch (err) {
      console.error('Error deleting structure:', err);
      showToast('Failed to delete structure', 'error');
    }
  };

  // Delete pod (all walls in the pod group)
  const handleDeletePod = async (podName) => {
    const podGroup = sceneData.walls.filter(w => {
      try { return JSON.parse(w.text_content || '{}').pod_group === podName; } catch { return false; }
    });
    if (!podGroup.length) return;
    if (!window.confirm(`Delete pod "${podName}" (${podGroup.length} panels)? All artwork will be moved to inventory.`)) return;
    try {
      for (const wall of podGroup) {
        if (wall.artwork?.length) {
          for (const art of wall.artwork) {
            await updateArtworkPosition(art.element_id, null, 0, 0);
          }
        }
        await deleteWall(wall.element_id);
      }
      const deletedIds = new Set(podGroup.map(w => w.element_id));
      setSceneData(prev => ({ ...prev, walls: (prev.walls || []).filter(w => !deletedIds.has(w.element_id)) }));
      showToast(`Pod "${podName}" deleted`, 'success');
    } catch (err) {
      console.error('Error deleting pod:', err);
      showToast('Failed to delete pod', 'error');
    }
  };

  // Move wall up/down in order
  const moveWall = async (wallId, direction) => {
    try {
      const wallIndex = sceneData.walls.findIndex(w => w.element_id === wallId);
      if (wallIndex === -1) return;
      
      const updatedWalls = [...sceneData.walls];
      
      if (direction === 'up' && wallIndex > 0) {
        // Swap with previous wall
        [updatedWalls[wallIndex], updatedWalls[wallIndex - 1]] = 
          [updatedWalls[wallIndex - 1], updatedWalls[wallIndex]];
        
        showToast('Wall moved up', 'success');
      } else if (direction === 'down' && wallIndex < updatedWalls.length - 1) {
        // Swap with next wall
        [updatedWalls[wallIndex], updatedWalls[wallIndex + 1]] = 
          [updatedWalls[wallIndex + 1], updatedWalls[wallIndex]];
        
        showToast('Wall moved down', 'success');
      }
      
      // Update state
      setSceneData({ ...sceneData, walls: updatedWalls });
      
      // Save wall order to database
      await updateWallOrder(collectionId, updatedWalls);
    } catch (error) {
      console.error('Error moving wall:', error);
      showToast('Wall order changed but not saved to database', 'warning');
    }
  };
  
  // Load previous collections for copy walls feature
  const loadPreviousCollections = async () => {
    try {
      setIsLoadingPreviousCollections(true);
      const result = await getPreviousGalleryCollections(collectionId);
      setGalleryInfo(result.gallery);
      setPreviousCollections(result.previousCollections);
      setShowCopyWallsModal(true);
    } catch (error) {
      console.error('Error loading previous collections:', error);
      showToast('Failed to load previous collections', 'error');
    } finally {
      setIsLoadingPreviousCollections(false);
    }
  };
  
  // Handle copying walls from a previous collection
  const handleCopyWalls = async (sourceCollectionId) => {
    try {
      setIsCopyingWalls(true);
      const result = await copyWallsFromCollection(sourceCollectionId, collectionId);
      
      if (result.success) {
        showToast(result.message, 'success');
        setShowCopyWallsModal(false);
        // Reload scene data to show new walls
        const responseData = await fetchSceneElements(collectionId, '2d');
        setSceneData(responseData);
      } else {
        showToast(result.message, 'warning');
      }
    } catch (error) {
      console.error('Error copying walls:', error);
      showToast('Failed to copy walls: ' + error.message, 'error');
    } finally {
      setIsCopyingWalls(false);
    }
  };
  
  // Handle saving the entire layout
  const saveLayout = async () => {
    try {
      const saveBtn = document.getElementById('save-btn');
      if (saveBtn) {
        saveBtn.innerHTML = '<span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span> Saving...';
        saveBtn.disabled = true;
      }
      
      const savePromises = [];
      const successfulSaves = [];
      const failedSaves = [];
      
      // Get artwork from walls
      sceneData.walls.forEach(wall => {
        if (wall.artwork) {
          wall.artwork.forEach(art => {
            // Get the wall-specific positions
            const wallPosX = art.wall_position_x !== undefined ? art.wall_position_x : 
                            (art.position_x !== undefined ? art.position_x : 0);
            const wallPosY = art.wall_position_y !== undefined ? art.wall_position_y : 
                            (art.position_y !== undefined ? art.position_y : 0);
            
            // Create update promise
            const savePromise = updateArtworkPosition(art.element_id, wall.element_id, wallPosX, wallPosY)
              .then(() => {
                successfulSaves.push({id: art.element_id, vc_id: art.vc_id});
                return art.element_id;
              })
              .catch(error => {
                console.error(`Error saving artwork ${art.element_id}:`, error);
                failedSaves.push({id: art.element_id, vc_id: art.vc_id, error: error.message});
                return null;
              });
            
            savePromises.push(savePromise);
          });
        }
      });
      
      // Get unplaced artwork
      if (sceneData.unplaced) {
        sceneData.unplaced.forEach(art => {
          // Get positions
          const wallPosX = art.wall_position_x !== undefined ? art.wall_position_x : 
                          (art.position_x !== undefined ? art.position_x : 0);
          const wallPosY = art.wall_position_y !== undefined ? art.wall_position_y : 
                          (art.position_y !== undefined ? art.position_y : 0);
          
          // Create update promise
          const savePromise = updateArtworkPosition(art.element_id, null, wallPosX, wallPosY)
            .then(() => {
              successfulSaves.push({id: art.element_id, vc_id: art.vc_id});
              return art.element_id;
            })
            .catch(error => {
              console.error(`Error saving unplaced artwork ${art.element_id}:`, error);
              failedSaves.push({id: art.element_id, vc_id: art.vc_id, error: error.message});
              return null;
            });
          
          savePromises.push(savePromise);
        });
      }
      
      // Wait for all updates to complete
      await Promise.allSettled(savePromises);
      
      // Reset button state
      if (saveBtn) {
        saveBtn.innerHTML = 'Save Layout';
        saveBtn.disabled = false;
      }
      
      // Show results
      if (failedSaves.length === 0) {
        showToast(`Layout saved successfully! (${successfulSaves.length} items updated)`, 'success');
      } else {
        showToast(`Partially saved: ${successfulSaves.length} items updated, ${failedSaves.length} failed.`, 'warning');
      }
    } catch (error) {
      console.error('Error saving layout:', error);
      showToast('Failed to save layout. Please try again.', 'error');
      
      // Reset button state
      const saveBtn = document.getElementById('save-btn');
      if (saveBtn) {
        saveBtn.innerHTML = 'Save Layout';
        saveBtn.disabled = false;
      }
    }
  };
  
  // Apply Cloudinary transformations based on use case with HEIC support
  const getOptimizedImageUrl = (originalUrl, useCase = 'display') => {
    if (!originalUrl) {
      // Return a placeholder image if no URL provided
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+Cjx0ZXh0IHg9IjEwMCIgeT0iMTAwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5Q0EzQUYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD4KPC9zdmc+';
    }
    
    if (!originalUrl.includes('cloudinary.com')) {
      // For non-Cloudinary URLs, try to return the original, but add error handling
      return originalUrl;
    }
    
    // Different transformations for different use cases - preserve aspect ratio
    const transformations = {
      display: 'w_800,q_80,f_auto', // Good quality for display, HEIC support
      thumbnail: 'w_200,q_70,f_auto', // Small thumbnails for inventory, HEIC support
      drag: 'w_400,q_60,f_auto'      // Lower quality for dragging, HEIC support
    };
    
    // Apply the appropriate transformation
    const transform = transformations[useCase] || transformations.display;
    
    // Check if the URL already has transformations
    if (originalUrl.includes('/upload/')) {
      return originalUrl.replace('/upload/', `/upload/${transform}/`);
    }
    
    return originalUrl; // If URL structure is unexpected, return original
  };
  
  // Get wall data for editing
  const getWallForEditing = () => {
    console.log('🔧 Getting wall for editing:', editingWallId);
    if (!editingWallId) return null;
    const wall = sceneData.walls.find(w => w.element_id === editingWallId);
    console.log('🔧 Found wall for editing:', wall);
    return wall;
  };
  
  // Count total artworks
  const getTotalArtworkCount = () => {
    let count = 0;
    
    // Count artwork on walls
    sceneData.walls.forEach(wall => {
      if (wall.artwork && Array.isArray(wall.artwork)) {
        count += wall.artwork.length;
      }
    });
    
    // Count unplaced artwork
    if (sceneData.unplaced && Array.isArray(sceneData.unplaced)) {
      count += sceneData.unplaced.length;
    }
    
    return count;
  };
  
  // Helper to convert inches to feet and inches for display
  const formatDimension = (inches) => {
    const feet = Math.floor(inches / 12);
    const remainingInches = Math.round(inches % 12);
    if (feet === 0) return `${remainingInches}"`;
    if (remainingInches === 0) return `${feet}'`;
    return `${feet}'${remainingInches}"`;
  };
  
  // Print wall layout
  const printWallLayout = () => {
    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('Please allow popups to print the layout', 'error');
      return;
    }
    
    const collectionTitle = collectionInfo?.title || 'Wall Layout';
    
    // Build HTML content for printing
    let wallsHtml = '';
    
    sceneData.walls.forEach((wall, wallIndex) => {
      const wallWidth = parseFloat(wall.width) || 120;
      const wallHeight = parseFloat(wall.height) || 96;
      const eyeLevelFromTop = wallHeight - 57; // 57" from floor
      
      // Scale for display (fit on page)
      const maxDisplayWidth = 700;
      const displayScale = Math.min(maxDisplayWidth / wallWidth, 400 / wallHeight);
      const displayWidth = wallWidth * displayScale;
      const displayHeight = wallHeight * displayScale;
      
      let artworkHtml = '';
      if (wall.artwork && wall.artwork.length > 0) {
        wall.artwork.forEach((art, artIndex) => {
          const artX = (art.wall_position_x || 0) * displayScale;
          const artY = (art.wall_position_y || 0) * displayScale;
          const artW = (art.width || 24) * displayScale;
          const artH = (art.height || 24) * displayScale;
          
          // Calculate height from floor
          const bottomFromTop = (art.wall_position_y || 0) + (art.height || 24);
          const heightFromFloor = wallHeight - bottomFromTop;
          const centerFromFloor = wallHeight - (art.wall_position_y || 0) - ((art.height || 24) / 2);
          
          artworkHtml += `
            <div style="
              position: absolute;
              left: ${artX}px;
              top: ${artY}px;
              width: ${artW}px;
              height: ${artH}px;
              border: 2px solid #333;
              background: #f0f0f0;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 10px;
              text-align: center;
              overflow: hidden;
              box-sizing: border-box;
            ">
              <div style="padding: 2px;">
                <strong>${art.title || art.vc_id || artIndex + 1}</strong><br/>
                <span style="font-size: 8px;">${art.width}"×${art.height}"</span><br/>
                <span style="font-size: 8px; color: #666;">Floor: ${formatDimension(heightFromFloor)}</span>
              </div>
            </div>
          `;
        });
      }
      
      // Eye level line
      const eyeLevelY = eyeLevelFromTop * displayScale;
      const showEyeLevel = eyeLevelFromTop > 0 && eyeLevelFromTop < wallHeight;
      
      wallsHtml += `
        <div style="page-break-inside: avoid; margin-bottom: 40px;">
          <h2 style="margin: 0 0 10px 0; font-size: 18px;">${wall.source || 'Wall ' + (wallIndex + 1)}</h2>
          <p style="margin: 0 0 15px 0; font-size: 12px; color: #666;">
            Dimensions: ${formatDimension(wallWidth)} × ${formatDimension(wallHeight)} (${wallWidth}" × ${wallHeight}")
            &nbsp;|&nbsp; Artworks: ${wall.artwork?.length || 0}
          </p>
          
          <div style="
            position: relative;
            width: ${displayWidth}px;
            height: ${displayHeight}px;
            border: 2px solid #000;
            background: #fafafa;
            margin-bottom: 20px;
          ">
            ${showEyeLevel ? `
              <div style="
                position: absolute;
                top: ${eyeLevelY}px;
                left: 0;
                right: 0;
                border-top: 1px dashed #3b82f6;
              ">
                <span style="
                  position: absolute;
                  right: 5px;
                  top: -12px;
                  font-size: 9px;
                  color: #3b82f6;
                  background: white;
                  padding: 0 3px;
                ">Eye Level (57")</span>
              </div>
            ` : ''}
            ${artworkHtml}
          </div>
          
          ${wall.artwork && wall.artwork.length > 0 ? `
            <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 10px;">
              <thead>
                <tr style="background: #f0f0f0;">
                  <th style="border: 1px solid #ccc; padding: 5px; text-align: left;">#</th>
                  <th style="border: 1px solid #ccc; padding: 5px; text-align: left;">Title</th>
                  <th style="border: 1px solid #ccc; padding: 5px; text-align: left;">Artist</th>
                  <th style="border: 1px solid #ccc; padding: 5px; text-align: center;">Size</th>
                  <th style="border: 1px solid #ccc; padding: 5px; text-align: center;">From Left</th>
                  <th style="border: 1px solid #ccc; padding: 5px; text-align: center;">From Floor</th>
                  <th style="border: 1px solid #ccc; padding: 5px; text-align: center;">Center Height</th>
                </tr>
              </thead>
              <tbody>
                ${wall.artwork.map((art, i) => {
                  const bottomFromTop = (art.wall_position_y || 0) + (art.height || 24);
                  const heightFromFloor = wallHeight - bottomFromTop;
                  const centerFromFloor = wallHeight - (art.wall_position_y || 0) - ((art.height || 24) / 2);
                  return `
                    <tr>
                      <td style="border: 1px solid #ccc; padding: 5px;">${i + 1}</td>
                      <td style="border: 1px solid #ccc; padding: 5px;">${art.title || '-'}</td>
                      <td style="border: 1px solid #ccc; padding: 5px;">${art.artist_name || '-'}</td>
                      <td style="border: 1px solid #ccc; padding: 5px; text-align: center;">${art.width}"×${art.height}"</td>
                      <td style="border: 1px solid #ccc; padding: 5px; text-align: center;">${formatDimension(art.wall_position_x || 0)}</td>
                      <td style="border: 1px solid #ccc; padding: 5px; text-align: center;">${formatDimension(heightFromFloor)}</td>
                      <td style="border: 1px solid #ccc; padding: 5px; text-align: center;">${formatDimension(centerFromFloor)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          ` : '<p style="font-size: 11px; color: #666; font-style: italic;">No artwork on this wall</p>'}
        </div>
      `;
    });
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${collectionTitle} - Wall Layout</title>
        <style>
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            padding: 20px;
            max-width: 800px;
            margin: 0 auto;
          }
          h1 {
            font-size: 24px;
            margin-bottom: 5px;
          }
          .header-info {
            font-size: 12px;
            color: #666;
            margin-bottom: 30px;
          }
          @media print {
            body { padding: 0; }
            .no-print { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="no-print" style="margin-bottom: 20px;">
          <button onclick="window.print()" style="
            padding: 10px 20px;
            background: #4f46e5;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
          ">🖨️ Print</button>
          <button onclick="window.close()" style="
            padding: 10px 20px;
            background: #6b7280;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            margin-left: 10px;
          ">Close</button>
        </div>
        
        <h1>${collectionTitle}</h1>
        <p class="header-info">
          Wall Layout Guide &nbsp;|&nbsp; ${sceneData.walls.length} Wall${sceneData.walls.length !== 1 ? 's' : ''} 
          &nbsp;|&nbsp; Generated: ${new Date().toLocaleDateString()}
        </p>
        
        ${wallsHtml}
        
        <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc; font-size: 10px; color: #999;">
          <p>Generated by VibrantCast Wall Planner</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
    
    showToast('Print layout opened in new window', 'success');
  };
  
  // Generate PDF labels
  const goToPdfGenerator = () => {
    navigate(`/printReactCards/${collectionId}`);
  };
  
  // Print wall layouts as PDF
  const printWallLayouts = async () => {
    try {
      showToast('Generating PDF with artwork images...', 'info');
      
      // Helper function to load an image and return as base64
      const loadImage = (url) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/jpeg', 0.7));
            } catch (e) {
              console.warn('Could not convert image:', url, e);
              resolve(null);
            }
          };
          img.onerror = () => {
            console.warn('Could not load image:', url);
            resolve(null);
          };
          // Use a smaller version of the image for PDF
          const optimizedUrl = url?.includes('cloudinary') 
            ? url.replace('/upload/', '/upload/w_200,q_70/')
            : url;
          img.src = optimizedUrl;
        });
      };
      
      // Create new PDF document (landscape for better wall representation)
      const doc = new jsPDF('landscape', 'pt', 'letter');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;
      
      // Helper to format inches to feet and inches
      const formatFeetInches = (inches) => {
        const feet = Math.floor(inches / 12);
        const remainingInches = Math.round(inches % 12);
        if (feet === 0) return `${remainingInches}"`;
        if (remainingInches === 0) return `${feet}'`;
        return `${feet}' ${remainingInches}"`;
      };
      
      // Title page
      doc.setFontSize(24);
      doc.setTextColor(60, 60, 60);
      doc.text(collectionInfo?.title || 'Gallery Wall Layout', pageWidth / 2, 60, { align: 'center' });
      
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 85, { align: 'center' });
      doc.text(`Total Walls: ${sceneData.walls.length}`, pageWidth / 2, 100, { align: 'center' });
      
      // Eye level reference
      doc.setFontSize(10);
      doc.text('Eye Level: 57" (Museum Standard)', pageWidth / 2, 120, { align: 'center' });
      
      // Debug: Log the wall data structure
      console.log('📄 PDF Generation - sceneData.walls:', sceneData.walls);
      sceneData.walls.forEach((wall, i) => {
        console.log(`📄 Wall ${i + 1}:`, wall.name || wall.source, '- artwork array:', wall.artwork);
      });
      
      // Process each wall (using for...of for async/await support)
      for (let index = 0; index < sceneData.walls.length; index++) {
        const wall = sceneData.walls[index];
        doc.addPage('landscape');
        
        // Wall header
        doc.setFontSize(18);
        doc.setTextColor(60, 60, 60);
        doc.text(`Wall ${index + 1}: ${wall.source || wall.name || 'Unnamed Wall'}`, margin, 40);
        
        // Wall dimensions
        doc.setFontSize(11);
        doc.setTextColor(80, 80, 80);
        doc.text(`Dimensions: ${formatFeetInches(wall.width)} W × ${formatFeetInches(wall.height)} H`, margin, 58);
        doc.text(`Artworks: ${wall.artwork?.length || 0}`, margin + 250, 58);
        
        // Calculate scale to fit wall on page
        const availableWidth = pageWidth - (margin * 2);
        const availableHeight = pageHeight - 150; // Leave room for header and table
        const wallWidthInches = wall.width;
        const wallHeightInches = wall.height;
        
        // Scale: points per inch
        const scaleX = availableWidth / wallWidthInches;
        const scaleY = (availableHeight * 0.5) / wallHeightInches; // Use 50% of available height for wall
        const scale = Math.min(scaleX, scaleY, 3); // Cap at 3 points per inch
        
        const scaledWallWidth = wallWidthInches * scale;
        const scaledWallHeight = wallHeightInches * scale;
        const wallStartX = (pageWidth - scaledWallWidth) / 2;
        const wallStartY = 80;
        
        // Draw wall background
        doc.setFillColor(245, 245, 240);
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(1);
        doc.rect(wallStartX, wallStartY, scaledWallWidth, scaledWallHeight, 'FD');
        
        // Draw eye level line (57" from floor)
        const eyeLevelFromTop = wall.height - 57;
        if (eyeLevelFromTop > 0 && eyeLevelFromTop < wall.height) {
          const eyeLevelY = wallStartY + (eyeLevelFromTop * scale);
          doc.setDrawColor(200, 180, 180);
          doc.setLineWidth(0.5);
          doc.setLineDash([4, 4], 0);
          doc.line(wallStartX, eyeLevelY, wallStartX + scaledWallWidth, eyeLevelY);
          doc.setLineDash([], 0);
          
          // Label
          doc.setFontSize(7);
          doc.setTextColor(150, 130, 130);
          doc.text('57" eye level', wallStartX + scaledWallWidth + 5, eyeLevelY + 3);
        }
        
        // Pre-load all images for this wall
        const artworkImages = await Promise.all(
          (wall.artwork || []).map(async (artwork) => {
            const imageData = await loadImage(artwork.source);
            return { artwork, imageData };
          })
        );
        
        // Draw artworks on wall
        const artworkList = [];
        artworkImages.forEach(({ artwork, imageData }, artIdx) => {
          // Get position values - prefer wall_position if available
          const posX = artwork.wall_position_x !== undefined ? artwork.wall_position_x : 
                      (artwork.position_x !== undefined ? artwork.position_x : 0);
          const posY = artwork.wall_position_y !== undefined ? artwork.wall_position_y : 
                      (artwork.position_y !== undefined ? artwork.position_y : 0);
          
          const artX = wallStartX + (posX * scale);
          const artY = wallStartY + (posY * scale);
          const artW = artwork.width * scale;
          const artH = artwork.height * scale;
          
          // Draw artwork image or placeholder rectangle
          if (imageData) {
            try {
              doc.addImage(imageData, 'JPEG', artX, artY, artW, artH);
              // Add border around image
              doc.setDrawColor(100, 100, 100);
              doc.setLineWidth(0.5);
              doc.rect(artX, artY, artW, artH, 'S');
            } catch (imgErr) {
              console.warn('Could not add image to PDF:', imgErr);
              // Fallback to rectangle
              doc.setFillColor(240, 240, 240);
              doc.setDrawColor(100, 100, 100);
              doc.setLineWidth(0.5);
              doc.rect(artX, artY, artW, artH, 'FD');
            }
          } else {
            // Draw placeholder rectangle
            doc.setFillColor(240, 240, 240);
            doc.setDrawColor(100, 100, 100);
            doc.setLineWidth(0.5);
            doc.rect(artX, artY, artW, artH, 'FD');
          }
          
          // Add number label in corner
          doc.setFillColor(255, 255, 255);
          doc.setDrawColor(80, 80, 80);
          const labelSize = Math.min(14, artW * 0.3, artH * 0.3);
          doc.rect(artX + 2, artY + 2, labelSize, labelSize, 'FD');
          doc.setFontSize(Math.min(8, labelSize * 0.7));
          doc.setTextColor(60, 60, 60);
          const labelNum = artIdx + 1;
          doc.text(String(labelNum), artX + 2 + labelSize/2, artY + 2 + labelSize/2 + 2, { align: 'center' });
          
          // Calculate heights from floor
          const floorHeight = wall.height - posY - artwork.height;
          const centerHeight = floorHeight + (artwork.height / 2);
          
          // Add to artwork list
          artworkList.push({
            num: labelNum,
            wallName: wall.name || wall.source || `Wall ${index + 1}`,
            wallIndex: index + 1,
            title: artwork.title || 'Untitled',
            artist_name: artwork.artist_name,
            width: artwork.width,
            height: artwork.height,
            floorHeight: floorHeight,
            centerHeight: centerHeight
          });
        });
        
        // Draw artwork table below wall
        if (artworkList.length > 0) {
          const tableStartY = wallStartY + scaledWallHeight + 30;
          
          // Table header
          doc.setFontSize(10);
          doc.setTextColor(60, 60, 60);
          doc.setFillColor(240, 240, 240);
          doc.rect(margin, tableStartY, pageWidth - (margin * 2), 18, 'F');
          
          doc.text('#', margin + 10, tableStartY + 13);
          doc.text('Title', margin + 35, tableStartY + 13);
          doc.text('Artist', margin + 280, tableStartY + 13);
          doc.text('Size', margin + 380, tableStartY + 13);
          doc.text('Floor Height', margin + 480, tableStartY + 13);
          doc.text('Center Height', margin + 580, tableStartY + 13);
          
          // Table rows
          doc.setFontSize(9);
          doc.setTextColor(80, 80, 80);
          let rowY = tableStartY + 18;
          
          artworkList.forEach((art, i) => {
            if (rowY > pageHeight - 30) {
              doc.addPage('landscape');
              rowY = 40;
            }
            
            // Alternating row background
            if (i % 2 === 1) {
              doc.setFillColor(250, 250, 250);
              doc.rect(margin, rowY, pageWidth - (margin * 2), 16, 'F');
            }
            
            doc.text(String(art.num), margin + 10, rowY + 12);
            
            // Truncate title if too long
            const maxTitleWidth = 230;
            let displayTitle = art.title;
            while (doc.getTextWidth(displayTitle) > maxTitleWidth && displayTitle.length > 10) {
              displayTitle = displayTitle.substring(0, displayTitle.length - 4) + '...';
            }
            doc.text(displayTitle, margin + 35, rowY + 12);
            
            // Truncate artist name if too long
            const maxArtistWidth = 90;
            let displayArtist = art.artist_name || '-';
            while (doc.getTextWidth(displayArtist) > maxArtistWidth && displayArtist.length > 10) {
              displayArtist = displayArtist.substring(0, displayArtist.length - 4) + '...';
            }
            doc.text(displayArtist, margin + 280, rowY + 12);
            doc.text(`${formatFeetInches(art.width)} × ${formatFeetInches(art.height)}`, margin + 380, rowY + 12);
            doc.text(formatFeetInches(art.floorHeight), margin + 480, rowY + 12);
            doc.text(formatFeetInches(art.centerHeight), margin + 580, rowY + 12);
            
            rowY += 16;
          });
        }
      }
      
      // Complete Artwork List at the end
      const allArtworks = [];
      sceneData.walls.forEach((wall, wallIndex) => {
        (wall.artwork || []).forEach((artwork, artIdx) => {
          const posY = artwork.wall_position_y !== undefined ? artwork.wall_position_y : 
                      (artwork.position_y !== undefined ? artwork.position_y : 0);
          const floorHeight = wall.height - posY - artwork.height;
          const centerHeight = floorHeight + (artwork.height / 2);
          
          allArtworks.push({
            wallNum: wallIndex + 1,
            wallName: wall.name || wall.source || `Wall ${wallIndex + 1}`,
            artNum: artIdx + 1,
            title: artwork.title || 'Untitled',
            artist_name: artwork.artist_name,
            width: artwork.width,
            height: artwork.height,
            floorHeight: floorHeight,
            centerHeight: centerHeight
          });
        });
      });
      
      if (allArtworks.length > 0) {
        doc.addPage('landscape');
        
        // Summary header
        doc.setFontSize(18);
        doc.setTextColor(60, 60, 60);
        doc.text('Complete Artwork List', margin, 40);
        
        doc.setFontSize(11);
        doc.setTextColor(100, 100, 100);
        doc.text(`Total Artworks: ${allArtworks.length}`, margin, 58);
        
        // Table header
        const tableStartY = 75;
        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
        doc.setFillColor(240, 240, 240);
        doc.rect(margin, tableStartY, pageWidth - (margin * 2), 18, 'F');
        
        doc.text('Wall', margin + 10, tableStartY + 13);
        doc.text('#', margin + 70, tableStartY + 13);
        doc.text('Title', margin + 95, tableStartY + 13);
        doc.text('Artist', margin + 340, tableStartY + 13);
        doc.text('Size', margin + 440, tableStartY + 13);
        doc.text('Floor', margin + 540, tableStartY + 13);
        doc.text('Center', margin + 620, tableStartY + 13);
        
        // Table rows
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        let rowY = tableStartY + 18;
        
        allArtworks.forEach((art, i) => {
          if (rowY > pageHeight - 30) {
            doc.addPage('landscape');
            rowY = 40;
            
            // Re-add header on new page
            doc.setFontSize(10);
            doc.setTextColor(60, 60, 60);
            doc.setFillColor(240, 240, 240);
            doc.rect(margin, rowY, pageWidth - (margin * 2), 18, 'F');
            
            doc.text('Wall', margin + 10, rowY + 13);
            doc.text('#', margin + 70, rowY + 13);
            doc.text('Title', margin + 95, rowY + 13);
            doc.text('Artist', margin + 340, rowY + 13);
            doc.text('Size', margin + 440, rowY + 13);
            doc.text('Floor', margin + 540, rowY + 13);
            doc.text('Center', margin + 620, rowY + 13);
            
            doc.setFontSize(9);
            doc.setTextColor(80, 80, 80);
            rowY += 18;
          }
          
          // Alternating row background
          if (i % 2 === 1) {
            doc.setFillColor(250, 250, 250);
            doc.rect(margin, rowY, pageWidth - (margin * 2), 16, 'F');
          }
          
          doc.text(`Wall ${art.wallNum}`, margin + 10, rowY + 12);
          doc.text(String(art.artNum), margin + 70, rowY + 12);
          
          // Truncate title if too long
          const maxTitleWidth = 230;
          let displayTitle = art.title;
          while (doc.getTextWidth(displayTitle) > maxTitleWidth && displayTitle.length > 10) {
            displayTitle = displayTitle.substring(0, displayTitle.length - 4) + '...';
          }
          doc.text(displayTitle, margin + 95, rowY + 12);
          
          // Truncate artist name if too long
          const maxArtistWidth2 = 90;
          let displayArtist2 = art.artist_name || '-';
          while (doc.getTextWidth(displayArtist2) > maxArtistWidth2 && displayArtist2.length > 10) {
            displayArtist2 = displayArtist2.substring(0, displayArtist2.length - 4) + '...';
          }
          doc.text(displayArtist2, margin + 340, rowY + 12);
          doc.text(`${formatFeetInches(art.width)} × ${formatFeetInches(art.height)}`, margin + 440, rowY + 12);
          doc.text(formatFeetInches(art.floorHeight), margin + 540, rowY + 12);
          doc.text(formatFeetInches(art.centerHeight), margin + 620, rowY + 12);
          
          rowY += 16;
        });
      }
      
      // Save the PDF
      const filename = `Wall_Layout_${collectionInfo?.title?.replace(/[^a-zA-Z0-9]/g, '_') || collectionId}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      
      showToast('Wall layout PDF generated successfully', 'success');
    } catch (error) {
      console.error('Error generating wall layout PDF:', error);
      showToast('Failed to generate PDF', 'error');
    }
  };

  // Print easel layouts as PDF (grouped)
  const printEaselLayouts = async () => {
    try {
      const allEasels = sceneData?.easels || [];
      if (allEasels.length === 0) {
        showToast('No easels to print', 'info');
        return;
      }
      showToast('Generating Easel Layout PDF...', 'info');

      // Helper function to load an image and return as base64
      const loadImage = (url) => {
        return new Promise((resolve) => {
          const img = new window.Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              resolve(canvas.toDataURL('image/jpeg', 0.7));
            } catch (e) { resolve(null); }
          };
          img.onerror = () => resolve(null);
          const optimizedUrl = url?.includes('cloudinary')
            ? url.replace('/upload/', '/upload/w_300,q_70/')
            : url;
          img.src = optimizedUrl;
        });
      };

      const formatFeetInches = (inches) => {
        const feet = Math.floor(inches / 12);
        const remainingInches = Math.round(inches % 12);
        if (feet === 0) return `${remainingInches}"`;
        if (remainingInches === 0) return `${feet}'`;
        return `${feet}' ${remainingInches}"`;
      };

      // Build easel groups from text_content metadata
      const groups = {};
      const ungrouped = [];
      // Also look up easel_2d records from wallData for text_content
      const easelRecords = wallData?.filter(w => w.type === 'easel') || [];

      allEasels.forEach(easel => {
        let groupName = null;
        // Check text_content on the sceneData easel or the wallData record
        const rec = easelRecords.find(r => String(r.element_id) === String(easel.element_id));
        const tc = easel.text_content || rec?.text_content;
        try {
          const meta = tc ? JSON.parse(tc) : null;
          if (meta?.easel_group) groupName = meta.easel_group;
        } catch { /* ok */ }
        if (groupName) {
          if (!groups[groupName]) groups[groupName] = [];
          groups[groupName].push(easel);
        } else {
          ungrouped.push(easel);
        }
      });

      const doc = new jsPDF('landscape', 'pt', 'letter');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 40;

      // Title page
      doc.setFontSize(24);
      doc.setTextColor(60, 60, 60);
      doc.text(collectionInfo?.title || 'Gallery Easel Layout', pageWidth / 2, 60, { align: 'center' });

      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, 85, { align: 'center' });
      doc.text(`Total Easels: ${allEasels.length}`, pageWidth / 2, 100, { align: 'center' });

      const groupNames = Object.keys(groups).sort();
      if (groupNames.length > 0) {
        doc.text(`Easel Groups: ${groupNames.join(', ')}`, pageWidth / 2, 115, { align: 'center' });
      }
      if (ungrouped.length > 0) {
        doc.text(`Ungrouped Easels: ${ungrouped.length}`, pageWidth / 2, groupNames.length > 0 ? 130 : 115, { align: 'center' });
      }

      // Render a group of easels on pages (grid layout, 3 cols × 2 rows per page)
      const renderEaselGroup = async (groupLabel, easelList) => {
        const cols = 3;
        const rows = 2;
        const perPage = cols * rows;
        const cellWidth = (pageWidth - margin * 2) / cols;
        const cellHeight = (pageHeight - 100) / rows; // Leave room for header

        for (let pageStart = 0; pageStart < easelList.length; pageStart += perPage) {
          doc.addPage('landscape');
          const pageSlice = easelList.slice(pageStart, pageStart + perPage);

          // Group header
          doc.setFontSize(16);
          doc.setTextColor(60, 60, 60);
          doc.text(groupLabel, margin, 35);
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`${easelList.length} easel${easelList.length !== 1 ? 's' : ''}`, margin + doc.getTextWidth(groupLabel) + 12, 35);

          for (let i = 0; i < pageSlice.length; i++) {
            const easel = pageSlice[i];
            const col = i % cols;
            const row = Math.floor(i / cols);
            const cellX = margin + col * cellWidth;
            const cellY = 50 + row * cellHeight;

            // Cell border
            doc.setDrawColor(220, 220, 220);
            doc.setLineWidth(0.5);
            doc.rect(cellX + 4, cellY + 4, cellWidth - 8, cellHeight - 8, 'S');

            // Easel name
            doc.setFontSize(11);
            doc.setTextColor(60, 60, 60);
            doc.text(easel.source || 'Unnamed Easel', cellX + 12, cellY + 20);

            // Easel info
            doc.setFontSize(8);
            doc.setTextColor(120, 120, 120);
            doc.text(`Center: ${easel.position_y || 60}" | Rot: ${easel.rotation_y || 0}°`, cellX + 12, cellY + 32);

            // Artwork
            const art = (easel.artwork && easel.artwork.length > 0) ? easel.artwork[0] : null;
            if (art) {
              const imageData = await loadImage(art.source);
              const imgAreaX = cellX + 12;
              const imgAreaY = cellY + 40;
              const imgAreaW = cellWidth - 24;
              const imgAreaH = cellHeight - 90;

              // Scale artwork to fit the cell area maintaining aspect ratio
              const artAspect = (art.width || 20) / (art.height || 20);
              let imgW, imgH;
              if (artAspect > imgAreaW / imgAreaH) {
                imgW = imgAreaW;
                imgH = imgAreaW / artAspect;
              } else {
                imgH = imgAreaH;
                imgW = imgAreaH * artAspect;
              }
              const imgX = imgAreaX + (imgAreaW - imgW) / 2;
              const imgY = imgAreaY + (imgAreaH - imgH) / 2;

              if (imageData) {
                try {
                  doc.addImage(imageData, 'JPEG', imgX, imgY, imgW, imgH);
                  doc.setDrawColor(160, 160, 160);
                  doc.setLineWidth(0.5);
                  doc.rect(imgX, imgY, imgW, imgH, 'S');
                } catch {
                  doc.setFillColor(240, 240, 240);
                  doc.rect(imgX, imgY, imgW, imgH, 'F');
                }
              } else {
                doc.setFillColor(240, 240, 240);
                doc.rect(imgX, imgY, imgW, imgH, 'F');
              }

              // Artwork title + artist below image
              const textY = cellY + cellHeight - 38;
              doc.setFontSize(9);
              doc.setTextColor(50, 50, 50);
              let displayTitle = art.title || 'Untitled';
              if (doc.getTextWidth(displayTitle) > cellWidth - 24) {
                while (doc.getTextWidth(displayTitle + '...') > cellWidth - 24 && displayTitle.length > 5) {
                  displayTitle = displayTitle.slice(0, -1);
                }
                displayTitle += '...';
              }
              doc.text(displayTitle, cellX + 12, textY);

              doc.setFontSize(8);
              doc.setTextColor(100, 100, 100);
              doc.text(art.artist_name || 'Unknown artist', cellX + 12, textY + 11);
              doc.text(`${formatFeetInches(art.width || 0)} × ${formatFeetInches(art.height || 0)}`, cellX + 12, textY + 21);
            } else {
              // Empty easel placeholder
              doc.setFontSize(10);
              doc.setTextColor(180, 180, 180);
              doc.text('No artwork', cellX + cellWidth / 2, cellY + cellHeight / 2, { align: 'center' });
            }
          }
        }
      };

      // Render each group
      for (const groupName of groupNames) {
        await renderEaselGroup(groupName, groups[groupName]);
      }
      // Render ungrouped
      if (ungrouped.length > 0) {
        await renderEaselGroup(groupNames.length > 0 ? 'Ungrouped Easels' : 'All Easels', ungrouped);
      }

      // Summary table
      doc.addPage('landscape');
      doc.setFontSize(18);
      doc.setTextColor(60, 60, 60);
      doc.text('Easel Summary', margin, 40);

      const tableStartY = 60;
      doc.setFontSize(10);
      doc.setTextColor(60, 60, 60);
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, tableStartY, pageWidth - margin * 2, 18, 'F');

      doc.text('Easel', margin + 10, tableStartY + 13);
      doc.text('Group', margin + 160, tableStartY + 13);
      doc.text('Artwork', margin + 290, tableStartY + 13);
      doc.text('Artist', margin + 470, tableStartY + 13);
      doc.text('Size', margin + 600, tableStartY + 13);

      doc.setFontSize(9);
      doc.setTextColor(80, 80, 80);
      let rowY = tableStartY + 18;

      allEasels.forEach((easel, i) => {
        if (rowY > pageHeight - 30) {
          doc.addPage('landscape');
          rowY = 40;
          doc.setFontSize(10);
          doc.setFillColor(240, 240, 240);
          doc.setTextColor(60, 60, 60);
          doc.rect(margin, rowY, pageWidth - margin * 2, 18, 'F');
          doc.text('Easel', margin + 10, rowY + 13);
          doc.text('Group', margin + 160, rowY + 13);
          doc.text('Artwork', margin + 290, rowY + 13);
          doc.text('Artist', margin + 470, rowY + 13);
          doc.text('Size', margin + 600, rowY + 13);
          doc.setFontSize(9);
          doc.setTextColor(80, 80, 80);
          rowY += 18;
        }
        if (i % 2 === 1) {
          doc.setFillColor(250, 250, 250);
          doc.rect(margin, rowY, pageWidth - margin * 2, 16, 'F');
        }

        let groupLabel = '—';
        try {
          const meta = easel.text_content ? JSON.parse(easel.text_content) : null;
          if (meta?.easel_group) groupLabel = meta.easel_group;
        } catch { /* ok */ }

        const art = (easel.artwork && easel.artwork.length > 0) ? easel.artwork[0] : null;
        doc.text(easel.source || 'Unnamed', margin + 10, rowY + 12);
        doc.text(groupLabel, margin + 160, rowY + 12);
        doc.text(art?.title || '(empty)', margin + 290, rowY + 12);
        doc.text(art?.artist_name || '—', margin + 470, rowY + 12);
        doc.text(art ? `${formatFeetInches(art.width || 0)} × ${formatFeetInches(art.height || 0)}` : '—', margin + 600, rowY + 12);
        rowY += 16;
      });

      const filename = `Easel_Layout_${collectionInfo?.title?.replace(/[^a-zA-Z0-9]/g, '_') || collectionId}_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      showToast('Easel layout PDF generated successfully', 'success');
    } catch (error) {
      console.error('Error generating easel layout PDF:', error);
      showToast('Failed to generate PDF', 'error');
    }
  };
  
  // View 3D gallery
  const viewIn3D = () => {
    navigate(`/gallery-3d-editor?collection_id=${collectionId}`);
  };
  
  // View gallery detail
  const viewGalleryDetail = () => {
    navigate(`/curator/collections/${collectionId}`);
  };
  
  // Refresh the page data
  const refreshData = async () => {
    try {
      setIsLoading(true);
      showToast('Refreshing data...', 'info');
      await loadData();
      showToast('Data refreshed successfully', 'success');
    } catch (error) {
      console.error('Error refreshing data:', error);
      showToast('Failed to refresh data', 'error');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Search for artist artwork (reverse workflow)
  const handleArtistSearch = async (e) => {
    e.preventDefault();
    if (!artistSearchQuery.trim()) return;
    
    try {
      setIsSearchingArtist(true);
      const results = await searchArtistArtwork(artistSearchQuery.trim());
      setArtistSearchResults(results);
      
      if (!results.artist) {
        showToast(`No artist found with handle "${artistSearchQuery}"`, 'warning');
      } else if (results.artworks.length === 0) {
        showToast(`${results.artist.handle} has no public artwork for sale`, 'info');
      } else {
        showToast(`Found ${results.artworks.length} artworks by ${results.artist.handle}`, 'success');
      }
    } catch (error) {
      console.error('Error searching artist:', error);
      showToast('Failed to search artist', 'error');
    } finally {
      setIsSearchingArtist(false);
    }
  };
  
  // Add artwork from search results to collection
  const handleAddArtworkToCollection = async (artwork) => {
    try {
      setAddingArtworkId(artwork.vc_id);
      const result = await addArtworkToCollection(collectionId, artwork);
      
      if (result.existing) {
        showToast('Artwork is already in this collection', 'warning');
      } else if (result.success) {
        // Add the new element to unplaced artwork locally
        const newElement = {
          ...result.element,
          title: artwork.title,
          price: artwork.price,
          pendingApproval: result.pendingApproval // Track approval status
        };
        
        setSceneData(prev => ({
          ...prev,
          unplaced: [...(prev.unplaced || []), newElement]
        }));
        
        if (result.pendingApproval) {
          showToast(`Added "${artwork.title}" (pending curator approval)`, 'success');
        } else {
          showToast(`Added "${artwork.title}" to collection`, 'success');
        }
      }
    } catch (error) {
      console.error('Error adding artwork:', error);
      showToast('Failed to add artwork to collection', 'error');
    } finally {
      setAddingArtworkId(null);
    }
  };
  
  // Check if artwork is already in collection
  const isArtworkInCollection = (vcId) => {
    const inWalls = sceneData.walls.some(wall => 
      wall.artwork?.some(art => art.vc_id === vcId)
    );
    const inUnplaced = sceneData.unplaced?.some(art => art.vc_id === vcId);
    return inWalls || inUnplaced;
  };
  
  // Clear artist search
  const clearArtistSearch = () => {
    setArtistSearchQuery('');
    setArtistSearchResults(null);
    setArtworkFilterQuery(''); // Clear artwork filter too
    // Also clear from localStorage
    try {
      localStorage.removeItem('galleryWallPlanner_lastArtistSearch');
    } catch (error) {
      console.warn('Error clearing saved artist search:', error);
    }
  };
  
  // Update metadata and refresh
  const handleUpdateMetadata = async () => {
    if (!collectionId || isNaN(collectionId)) {
      console.warn('Invalid collectionId provided to handleUpdateMetadata:', collectionId);
      showToast('Invalid collection ID', 'error');
      return;
    }
    
    try {
      setIsUpdatingMetadata(true);
      showToast('Updating artwork metadata...', 'info');
      
      const result = await updateCollectionArtworkMetadata(collectionId);
      
      if (result.success) {
        showToast(result.message, 'success');
        // Auto-refresh after successful update
        await refreshData();
      } else {
        showToast('Failed to update metadata', 'error');
      }
    } catch (error) {
      console.error('Error updating metadata:', error);
      showToast('Failed to update metadata', 'error');
    } finally {
      setIsUpdatingMetadata(false);
    }
  };

  // Handle updating wall 3D position
  const handleUpdateWallPosition = async (updatedWall) => {
    try {
      console.log('🔄 GalleryWallPlanner: Updating wall position:', updatedWall);
      
      // Update the wall in the database
      const result = await updateWall(
        updatedWall.element_id,
        updatedWall.source,
        updatedWall.width,
        updatedWall.height,
        updatedWall.wall_center_x,
        updatedWall.wall_center_y,
        updatedWall.wall_center_z,
        updatedWall.wall_rotation_y,
        updatedWall.display_order,
        updatedWall.background_color,
        updatedWall.color,
        updatedWall.text_content
      );
      
      console.log('✅ GalleryWallPlanner: Update result:', result);
      
      // Update local state
      setSceneData(prevData => ({
        ...prevData,
        walls: prevData.walls.map(wall => 
          wall.element_id === updatedWall.element_id ? updatedWall : wall
        )
      }));
      
      // Sync artwork 3D positions after wall move/rotate
      try {
        await syncArtworkPositionsOnWall(updatedWall.element_id);
      } catch (syncErr) {
        console.warn('⚠️ Artwork sync after wall update failed:', syncErr);
      }
      
      showToast('Wall position updated successfully', 'success');
      
      // Return success result
      return { success: true, data: result };
    } catch (error) {
      console.error('❌ GalleryWallPlanner: Error updating wall position:', error);
      showToast('Failed to update wall position', 'error');
      throw error; // Re-throw to let TopDownWallPlanner handle it
    }
  };

  /**
   * Handle pod creation from TopDownWallPlanner
   */
  const handlePodSubmit = async (podData) => {
    try {
      const result = await addPod(
        collectionId,
        podData.name,
        podData.centerX,
        podData.centerZ,
        podData.rotationY,
        podData.wallWidth || 96,
        podData.wallHeight || 96,
        podData.tiltAngle || 70
      );

      // Add new walls to local state
      if (result.walls) {
        setSceneData(prevData => ({
          ...prevData,
          walls: [...prevData.walls, ...result.walls.map(w => ({ ...w, artwork: [] }))]
        }));
      }

      showToast(result.message, 'success');
      return result;
    } catch (error) {
      console.error('❌ Error creating pod:', error);
      showToast('Failed to create pod', 'error');
      throw error;
    }
  };

  /**
   * Handle easel creation from TopDownWallPlanner
   */
  const handleEaselSubmit = async (easelData) => {
    try {
      const result = await addEasel(
        collectionId,
        easelData.name,
        easelData.centerX,
        easelData.centerZ,
        easelData.rotationY
      );

      // Add new easel to local state
      if (result.easel) {
        setSceneData(prevData => ({
          ...prevData,
          easels: [...(prevData.easels || []), result.easel]
        }));
      }

      showToast(result.message, 'success');
      return result;
    } catch (error) {
      console.error('❌ Error creating easel:', error);
      showToast('Failed to create easel', 'error');
      throw error;
    }
  };

  /**
   * Handle easel position/rotation update from TopDownWallPlanner
   */
  const handleUpdateEaselPosition = async (updatedEasel) => {
    try {
      const updateData = {
        position_x: updatedEasel.position_x,
        position_y: updatedEasel.position_y || 0,
        position_z: updatedEasel.position_z,
        rotation_y: updatedEasel.rotation_y
      };
      if (updatedEasel.source !== undefined) updateData.source = updatedEasel.source;
      if (updatedEasel.text_content !== undefined) updateData.text_content = updatedEasel.text_content;

      const { error } = await supabase
        .from('scene_elements')
        .update(updateData)
        .eq('element_id', updatedEasel.element_id);
      if (error) throw error;

      // Update local state
      setSceneData(prevData => ({
        ...prevData,
        easels: (prevData.easels || []).map(e =>
          e.element_id === updatedEasel.element_id ? { ...e, ...updatedEasel } : e
        )
      }));

      showToast('Easel updated', 'success');
    } catch (error) {
      console.error('❌ Error updating easel position:', error);
      showToast('Failed to update easel position', 'error');
      throw error;
    }
  };

  /**
   * Handle structure creation from TopDownWallPlanner
   */
  const handleStructureSubmit = async (structureData) => {
    try {
      const result = await addStructure(
        collectionId,
        structureData.name,
        structureData.shape,
        structureData.width,
        structureData.height,
        structureData.depth,
        structureData.centerX,
        structureData.centerZ,
        structureData.rotationY,
        structureData.color
      );

      if (result.structure) {
        setSceneData(prevData => ({
          ...prevData,
          structures: [...(prevData.structures || []), result.structure]
        }));
      }

      showToast(result.message, 'success');
      return result;
    } catch (error) {
      console.error('❌ Error creating structure:', error);
      showToast('Failed to create structure', 'error');
      throw error;
    }
  };

  /**
   * Handle structure position/rotation/dimension update from TopDownWallPlanner
   */
  const handleUpdateStructurePosition = async (updatedStructure) => {
    try {
      if (updatedStructure._fullUpdate) {
        // Full property update (from edit panel)
        await updateStructure({
          element_id: updatedStructure.element_id,
          name: updatedStructure.source,
          shape: updatedStructure.shape,
          width: updatedStructure.width,
          height: updatedStructure.height,
          depth: updatedStructure.depth,
          position_x: updatedStructure.position_x,
          position_y: updatedStructure.position_y || 0,
          position_z: updatedStructure.position_z,
          rotation_y: updatedStructure.rotation_y,
          color: updatedStructure.color,
          text_content: updatedStructure.text_content
        });
      } else {
        // Position-only update (from drag)
        await updateSceneElement({
          element_id: updatedStructure.element_id,
          position_x: updatedStructure.position_x,
          position_y: updatedStructure.position_y || 0,
          position_z: updatedStructure.position_z,
          rotation_y: updatedStructure.rotation_y
        });
      }

      setSceneData(prevData => ({
        ...prevData,
        structures: (prevData.structures || []).map(s =>
          s.element_id === updatedStructure.element_id ? { ...s, ...updatedStructure } : s
        )
      }));

      showToast('Structure updated', 'success');
    } catch (error) {
      console.error('❌ Error updating structure:', error);
      showToast('Failed to update structure', 'error');
      throw error;
    }
  };

  /**
   * Handle dropping artwork onto an easel
   */
  const handleEaselDrop = async (e, easelId) => {
    e.preventDefault();
    if (!currentDraggedItem) return;

    try {
      const isFromNewInventory = currentDraggedItem.isFromInventory ||
        (currentDraggedItem.element_id === currentDraggedItem.vc_id && !currentDraggedItem.isInSceneElements);

      if (isFromNewInventory) {
        // Create scene_element and assign to easel
        const result = await createAndPlaceArtwork(
          collectionId,
          currentDraggedItem.vc_id,
          easelId,   // parent_element_id = easel
          0, 0,      // wall_position not used for easels
          currentDraggedItem
        );

        if (result.success) {
          // Reload to reflect the change
          const responseData = await fetchSceneElements(collectionId, '2d');
          setSceneData(responseData);
          loadInventoryData();
          showToast('Artwork placed on easel', 'success');
        }
      } else {
        // Existing artwork, just re-parent to the easel
        const { error: updateError } = await supabase
          .from('scene_elements')
          .update({ parent_element_id: easelId, wall_position_x: 0, wall_position_y: 0 })
          .eq('element_id', currentDraggedItem.element_id);

        if (updateError) throw updateError;

        // Reload to reflect
        const responseData = await fetchSceneElements(collectionId, '2d');
        setSceneData(responseData);
        showToast('Artwork placed on easel', 'success');
      }
    } catch (error) {
      console.error('Error placing artwork on easel:', error);
      showToast('Failed to place artwork on easel', 'error');
    }

    setCurrentDraggedItem(null);
  };

  /**
   * Remove artwork from easel (set parent_element_id to null)
   */
  const handleRemoveFromEasel = async (artworkElementId) => {
    try {
      const { error: updateError } = await supabase
        .from('scene_elements')
        .update({ parent_element_id: null, wall_position_x: null, wall_position_y: null })
        .eq('element_id', artworkElementId);

      if (updateError) throw updateError;

      const responseData = await fetchSceneElements(collectionId, '2d');
      setSceneData(responseData);
      loadInventoryData();
      showToast('Artwork removed from easel', 'success');
    } catch (error) {
      console.error('Error removing artwork from easel:', error);
      showToast('Failed to remove artwork from easel', 'error');
    }
  };
  
  // Open dimension edit modal for an artwork
  const handleEditDimensions = (artwork) => {
    console.log('📐 Opening dimension edit modal for:', artwork);
    setEditingArtworkDimensions(artwork);
    // Get hanger_height from artwork.more JSONB or from hanger_height property
    const hangerHeight = artwork.more?.hanger_height || artwork.hanger_height || '';
    setDimensionFormData({
      width: artwork.width?.toString() || '',
      height: artwork.height?.toString() || '',
      hanger_height: hangerHeight?.toString() || ''
    });
    setShowDimensionModal(true);
  };
  
  // Open measurements modal for an artwork (shows placement info + editable hanger height)
  const handleShowMeasurements = (artwork, wallWidth, wallHeight, wallArtworks = []) => {
    console.log('📏 Opening measurements modal for:', artwork);
    const hangerHeight = artwork.more?.hanger_height || artwork.hanger_height || '';
    setMeasurementsArtwork({ artwork, wallWidth, wallHeight, wallArtworks });
    setMeasurementsHangerHeight(hangerHeight?.toString() || '');
    setShowMeasurementsModal(true);
  };
  
  // Save hanger height from measurements modal (updates local state immediately, saves to DB)
  const handleSaveMeasurementsHangerHeight = async () => {
    if (!measurementsArtwork?.artwork?.vc_id) {
      showToast('Cannot update: artwork has no vc_id', 'error');
      return;
    }
    
    const newHangerHeight = measurementsHangerHeight ? parseFloat(measurementsHangerHeight) : null;
    const artworkHeight = measurementsArtwork.artwork.height;
    
    // Validate hanger height if provided
    if (newHangerHeight !== null && (isNaN(newHangerHeight) || newHangerHeight <= 0 || newHangerHeight > artworkHeight)) {
      showToast('Hanger height must be a positive number less than artwork height', 'error');
      return;
    }
    
    setIsSavingMeasurements(true);
    
    try {
      // 1. Update in database (only hanger_height, keep existing dimensions)
      await updateArtworkDimensions(
        measurementsArtwork.artwork.vc_id,
        measurementsArtwork.artwork.width,
        measurementsArtwork.artwork.height,
        newHangerHeight
      );
      
      // 2. Update local state immediately (no reload needed)
      setSceneData(prevData => ({
        ...prevData,
        walls: prevData.walls.map(wall => ({
          ...wall,
          artwork: wall.artwork?.map(art => 
            art.vc_id === measurementsArtwork.artwork.vc_id
              ? { 
                  ...art, 
                  hanger_height: newHangerHeight,
                  more: { ...art.more, hanger_height: newHangerHeight }
                }
              : art
          )
        }))
      }));
      
      // 3. Update the modal state so it shows new calculation
      setMeasurementsArtwork(prev => ({
        ...prev,
        artwork: {
          ...prev.artwork,
          hanger_height: newHangerHeight,
          more: { ...prev.artwork.more, hanger_height: newHangerHeight }
        }
      }));
      
      showToast(`Hanger height saved: ${newHangerHeight}" from top`, 'success');
    } catch (error) {
      console.error('Failed to save hanger height:', error);
      showToast(`Failed to save: ${error.message}`, 'error');
    } finally {
      setIsSavingMeasurements(false);
    }
  };
  
  // Save artwork dimensions
  const handleSaveDimensions = async () => {
    if (!editingArtworkDimensions?.vc_id) {
      showToast('Cannot update dimensions: artwork has no vc_id', 'error');
      return;
    }
    
    const newWidth = parseFloat(dimensionFormData.width);
    const newHeight = parseFloat(dimensionFormData.height);
    const newHangerHeight = dimensionFormData.hanger_height ? parseFloat(dimensionFormData.hanger_height) : null;
    
    if (isNaN(newWidth) || isNaN(newHeight) || newWidth <= 0 || newHeight <= 0) {
      showToast('Please enter valid dimensions (positive numbers)', 'error');
      return;
    }
    
    // Validate hanger height if provided
    if (newHangerHeight !== null && (isNaN(newHangerHeight) || newHangerHeight <= 0 || newHangerHeight > newHeight)) {
      showToast('Hanger height must be a positive number less than artwork height', 'error');
      return;
    }
    
    setIsSavingDimensions(true);
    
    try {
      // 1. Update dimensions in vcs table (includes hanger_height in more JSONB)
      await updateArtworkDimensions(editingArtworkDimensions.vc_id, newWidth, newHeight, newHangerHeight);
      
      // 2. Update dimensions in scene_elements (local state update for immediate feedback)
      setSceneData(prevData => ({
        ...prevData,
        walls: prevData.walls.map(wall => ({
          ...wall,
          artwork: wall.artwork?.map(art => 
            art.vc_id === editingArtworkDimensions.vc_id
              ? { 
                  ...art, 
                  width: newWidth, 
                  height: newHeight,
                  hanger_height: newHangerHeight,
                  more: { ...art.more, hanger_height: newHangerHeight }
                }
              : art
          )
        }))
      }));
      
      // 3. Sync scene elements metadata from vcs for consistency
      await updateCollectionArtworkMetadata(collectionId);
      
      const hangerMsg = newHangerHeight ? ` (hanger: ${newHangerHeight}" from top)` : '';
      showToast(`Dimensions updated to ${newWidth}" × ${newHeight}"${hangerMsg}`, 'success');
      setShowDimensionModal(false);
      setEditingArtworkDimensions(null);
      
    } catch (error) {
      console.error('Failed to save dimensions:', error);
      showToast(`Failed to save dimensions: ${error.message}`, 'error');
    } finally {
      setIsSavingDimensions(false);
    }
  };
  
  // Render loading state
  if (isLoading) {
    return (
      <div id="loading-overlay" className="loading-overlay">
        <div className="loading-card">
          <div className="spinner"></div>
          <h2 className="text-lg font-bold mb-2">Loading Gallery Wall</h2>
          <p className="text-gray-600">Please wait while we prepare your artwork...</p>
        </div>
      </div>
    );
  }
  
  // Render error state
  if (error && !collectionId) {
    return (
      <div className="gallery-wall-planner">
        <div className="error-container p-6 max-w-md mx-auto mt-10">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-700 mb-4">No collection ID provided. Please select a collection first.</p>
          <button 
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
            onClick={() => navigate('/')}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="gallery-wall-planner">
      {/* Status message — single non-blocking bar */}
      {statusMsg && (
        <div className={`status-toast status-toast-${statusMsg.type}`} key={statusMsg.id}>
          {statusMsg.type === 'success' && '✓ '}
          {statusMsg.type === 'error' && '✗ '}
          {statusMsg.message}
        </div>
      )}
      
      {/* Top Controls Bar - Updated Layout */}
      <div className="top-controls-bar">
        <div className="controls-left">
          <div>
            {collectionInfo && (
              <h2 className="text-xs text-gray-400">{collectionInfo.title}</h2>
            )}
          </div>
        </div>
      </div>
      
      {/* Simplified Buttons Row */}
      <div className="buttons-row">
        <div className="buttons-container">
          {/* Done button (was Return) */}
          <button 
            onClick={viewGalleryDetail}
            className="control-btn done-btn"
            style={{ backgroundColor: '#6b7280' }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Done
          </button>
          
          {/* Add Wall */}
          <button 
            id="add-wall-btn" 
            className="control-btn add-wall-btn"
            onClick={() => {
              setEditingWallId(null);
              setShowWallModal(true);
            }}
          >
            + Add Wall
          </button>
          
          {/* Curator Tools Dropdown */}
          <div className="curator-tools-dropdown" ref={curatorToolsRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button
              className="control-btn curator-tools-btn"
              onClick={() => setShowCuratorTools(!showCuratorTools)}
              style={{ 
                backgroundColor: '#7c3aed',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Palette size={16} />
              <span>Tools</span>
              <ChevronDown size={14} style={{ transform: showCuratorTools ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
            </button>
            
            {showCuratorTools && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                minWidth: '200px',
                zIndex: 1000,
                overflow: 'hidden'
              }}>
                <button
                  onClick={() => { viewIn3D(); setShowCuratorTools(false); }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: 'left'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Zap size={18} style={{ color: '#7c3aed' }} />
                  <span>3D Gallery View</span>
                </button>
                
                <button
                  onClick={() => { setShowTopDownPlanner(true); setShowCuratorTools(false); }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: 'left'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Layers size={18} style={{ color: '#7c3aed' }} />
                  <span>Top-Down View</span>
                </button>
                
                <button
                  onClick={() => { goToPdfGenerator(); setShowCuratorTools(false); }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: 'left'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  <span>Generate Labels</span>
                </button>
                
                <button
                  onClick={() => { printWallLayouts(); setShowCuratorTools(false); }}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    textAlign: 'left'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Printer size={18} style={{ color: '#7c3aed' }} />
                  <span>Print Wall Layouts</span>
                </button>
                
                <button
                  onClick={() => { loadPreviousCollections(); setShowCuratorTools(false); }}
                  disabled={isLoadingPreviousCollections}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: 'none',
                    background: 'none',
                    cursor: isLoadingPreviousCollections ? 'wait' : 'pointer',
                    fontSize: '13px',
                    textAlign: 'left',
                    opacity: isLoadingPreviousCollections ? 0.6 : 1
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <Copy size={18} style={{ color: '#7c3aed' }} />
                  <span>{isLoadingPreviousCollections ? 'Loading...' : 'Copy Walls from Previous'}</span>
                </button>
                
                <div style={{ borderTop: '1px solid #e5e7eb', margin: '4px 0' }} />
                
                <button
                  onClick={() => { handleUpdateMetadata(); setShowCuratorTools(false); }}
                  disabled={isUpdatingMetadata || isLoading}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: 'none',
                    background: 'none',
                    cursor: isUpdatingMetadata ? 'wait' : 'pointer',
                    fontSize: '13px',
                    textAlign: 'left',
                    opacity: isUpdatingMetadata ? 0.6 : 1
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <RotateCcw size={18} style={{ color: '#6b7280' }} />
                  <span>{isUpdatingMetadata ? 'Updating...' : 'Update Metadata'}</span>
                </button>
                
                <button
                  onClick={() => { refreshData(); setShowCuratorTools(false); }}
                  disabled={isLoading || isUpdatingMetadata}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    border: 'none',
                    background: 'none',
                    cursor: isLoading ? 'wait' : 'pointer',
                    fontSize: '13px',
                    textAlign: 'left',
                    opacity: isLoading ? 0.6 : 1
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <RefreshCw size={18} style={{ color: '#6b7280' }} />
                  <span>{isLoading ? 'Refreshing...' : 'Refresh'}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Main container with sidebar */}
      <div className="flex">
        {/* Main content */}
        <div 
          className={`main-content ${isSidebarCollapsed ? 'full-width' : ''}`}
          style={{ marginRight: isSidebarCollapsed ? '0' : '280px' }}
        >
          
          {/* Walls */}
          <div id="walls-container" ref={wallsContainerRef}>
            <div id="walls" className="space-y-12 pb-16">
              {sceneData.walls.length === 0 ? (
                <div className="p-8 border-2 border-dashed border-gray-300 rounded-lg text-center">
                  <p className="text-gray-500 italic mb-4">No walls created yet. Click "Add Wall" to get started.</p>
                  <button 
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                    onClick={() => {
                      setEditingWallId(null);
                      setShowWallModal(true);
                    }}
                  >
                    Add Your First Wall
                  </button>
                </div>
              ) : (
                sceneData.walls.map((wall, index) => (
                  <WallComponent
                    key={wall.element_id}
                    wall={wall}
                    index={index}
                    totalWalls={sceneData.walls.length}
                    onDrop={handleDrop}
                    onEdit={() => editWall(wall.element_id)}
                    onDelete={() => handleDeleteWall(wall.element_id)}
                    onMoveUp={() => moveWall(wall.element_id, 'up')}
                    onMoveDown={() => moveWall(wall.element_id, 'down')}
                    onRemoveArtwork={removeArtwork}
                    onRepositionArtwork={handleArtworkReposition}
                    onNudgeArtwork={handleNudgeArtwork}
                    onEditDimensions={handleEditDimensions}
                    onShowMeasurements={handleShowMeasurements}
                    scale={scale}
                    getOptimizedImageUrl={getOptimizedImageUrl}
                    audioMap={artworkAudioMap}
                  />
                ))
              )}
            </div>
          </div>

          {/* Easels Section */}
          {(sceneData.easels || []).length > 0 && (
            <div style={{ padding: '16px 0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#92400e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                🎨 Easels ({sceneData.easels.length})
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                {sceneData.easels.map(easel => {
                  // Artwork attached to this easel (from API response)
                  const easelArtwork = (easel.artwork && easel.artwork.length > 0) ? easel.artwork[0] : null;

                  return (
                    <div
                      key={`easel-card-${easel.element_id}`}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleEaselDrop(e, easel.element_id)}
                      style={{
                        width: '200px',
                        border: '2px dashed #d97706',
                        borderRadius: '8px',
                        padding: '12px',
                        backgroundColor: '#fffbeb',
                        textAlign: 'center'
                      }}
                    >
                      <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '8px', color: '#92400e' }}>
                        {easel.source || 'Unnamed Easel'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#78350f', marginBottom: '8px' }}>
                        Rot: {easel.rotation_y || 0}° | Center: {easel.position_y || 60}"
                      </div>

                      {easelArtwork ? (
                        <div style={{ position: 'relative' }}>
                          <img
                            src={getCloudinaryUrl ? getCloudinaryUrl(easelArtwork.source, 200) : easelArtwork.source}
                            alt={easelArtwork.title || 'Artwork'}
                            style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain', borderRadius: '4px' }}
                          />
                          <div style={{ fontSize: '11px', color: '#374151', marginTop: '4px' }}>
                            {easelArtwork.title || 'Untitled'}
                          </div>
                          <button
                            onClick={() => handleRemoveFromEasel(easelArtwork.element_id)}
                            style={{
                              marginTop: '6px',
                              padding: '4px 10px',
                              fontSize: '11px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div style={{
                          padding: '20px',
                          border: '1px dashed #d1d5db',
                          borderRadius: '4px',
                          color: '#9ca3af',
                          fontSize: '12px'
                        }}>
                          Drag artwork here
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Fixed sidebar */}
        <div 
          className={`sidebar ${isSidebarCollapsed ? 'collapsed' : ''}`}
          style={{
            transform: isSidebarCollapsed ? 'translateX(100%)' : 'translateX(0)',
            width: isSidebarCollapsed ? '0' : '280px',
            padding: isSidebarCollapsed ? '0' : '20px',
            border: isSidebarCollapsed ? 'none' : '1px solid #ddd',
            boxShadow: isSidebarCollapsed ? 'none' : '-2px 0 10px rgba(0,0,0,0.1)',
            visibility: isSidebarCollapsed ? 'hidden' : 'visible',
            opacity: isSidebarCollapsed ? '0' : '1'
          }}
        >
          {/* Collapse button attached to sidebar */}
          <button
            onClick={() => setIsSidebarCollapsed(true)}
            style={{
              position: 'absolute',
              left: '-12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '24px',
              height: '48px',
              backgroundColor: '#6b7280',
              border: 'none',
              borderRadius: '4px 0 0 4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '-2px 0 4px rgba(0,0,0,0.1)'
            }}
            title="Hide Panel"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
          
          {!isSidebarCollapsed && (
            <>
              {/* Tab Toggle for Inventory vs Artist Search */}
              <div className="sidebar-tabs" style={{ display: 'flex', marginBottom: '12px', gap: '4px' }}>
                <button
                  onClick={() => setShowArtistSearch(false)}
                  className={`sidebar-tab ${!showArtistSearch ? 'active' : ''}`}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: !showArtistSearch ? '#4f46e5' : '#e5e7eb',
                    color: !showArtistSearch ? 'white' : '#374151'
                  }}
                >
                  Inventory
                </button>
                <button
                  onClick={() => setShowArtistSearch(true)}
                  className={`sidebar-tab ${showArtistSearch ? 'active' : ''}`}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    fontSize: '11px',
                    fontWeight: '500',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: showArtistSearch ? '#4f46e5' : '#e5e7eb',
                    color: showArtistSearch ? 'white' : '#374151'
                  }}
                >
                  🔍 Find Artist
                </button>
              </div>
              
              {showArtistSearch ? (
                /* Artist Search Panel */
                <div className="artist-search-panel">
                  <form onSubmit={handleArtistSearch} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <input
                        type="text"
                        value={artistSearchQuery}
                        onChange={(e) => setArtistSearchQuery(e.target.value)}
                        placeholder="Name, email, or handle..."
                        style={{
                          flex: 1,
                          padding: '8px',
                          fontSize: '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '4px'
                        }}
                      />
                      <button
                        type="submit"
                        disabled={isSearchingArtist || !artistSearchQuery.trim()}
                        style={{
                          padding: '8px 12px',
                          fontSize: '12px',
                          backgroundColor: '#4f46e5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: isSearchingArtist ? 'wait' : 'pointer',
                          opacity: isSearchingArtist || !artistSearchQuery.trim() ? 0.6 : 1
                        }}
                      >
                        {isSearchingArtist ? '...' : 'Search'}
                      </button>
                    </div>
                  </form>
                  
                  {artistSearchResults && (
                    <div className="search-results">
                      {/* Show multiple matching artists if more than one */}
                      {artistSearchResults.allArtists && artistSearchResults.allArtists.length > 1 && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px' }}>
                            {artistSearchResults.allArtists.length} artists found:
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {artistSearchResults.allArtists.map(artist => (
                              <button
                                key={artist.id}
                                onClick={async () => {
                                  try {
                                    setIsSearchingArtist(true);
                                    setArtworkFilterQuery(''); // Clear filter when switching artists
                                    const results = await getArtistArtworkByUuid(artist.uuid, artist);
                                    setArtistSearchResults({
                                      ...results,
                                      allArtists: artistSearchResults.allArtists
                                    });
                                  } catch (error) {
                                    console.error('Error loading artist:', error);
                                    showToast('Failed to load artist artwork', 'error');
                                  } finally {
                                    setIsSearchingArtist(false);
                                  }
                                }}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '10px',
                                  backgroundColor: artistSearchResults.artist?.id === artist.id ? '#4f46e5' : '#e5e7eb',
                                  color: artistSearchResults.artist?.id === artist.id ? 'white' : '#374151',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                {artist.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {artistSearchResults.artist && (
                        <div style={{ 
                          padding: '8px', 
                          backgroundColor: '#f3f4f6', 
                          borderRadius: '4px',
                          marginBottom: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '600' }}>
                              {artistSearchResults.artist.name}
                              {artistSearchResults.artist.handle && (
                                <span style={{ fontWeight: '400', color: '#6b7280' }}> @{artistSearchResults.artist.handle}</span>
                              )}
                            </div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                              {artistSearchResults.artworks.length} artwork{artistSearchResults.artworks.length !== 1 ? 's' : ''} for sale
                            </div>
                          </div>
                          <button
                            onClick={clearArtistSearch}
                            style={{
                              padding: '4px 8px',
                              fontSize: '10px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            Clear
                          </button>
                        </div>
                      )}
                      
                      {/* Artwork filter/search within artist */}
                      {artistSearchResults.artworks && artistSearchResults.artworks.length > 5 && (
                        <div style={{ marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={artworkFilterQuery}
                            onChange={(e) => setArtworkFilterQuery(e.target.value)}
                            placeholder="Filter by title..."
                            style={{
                              width: '100%',
                              padding: '6px 8px',
                              fontSize: '11px',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                      )}
                      
                      <div style={{ maxHeight: 'calc(100vh - 400px)', overflowY: 'auto' }}>
                        {artistSearchResults.artworks
                          .filter(artwork => 
                            !artworkFilterQuery.trim() || 
                            artwork.title?.toLowerCase().includes(artworkFilterQuery.toLowerCase())
                          )
                          .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
                          .map(artwork => {
                          const alreadyAdded = isArtworkInCollection(artwork.vc_id);
                          return (
                            <div 
                              key={artwork.vc_id}
                              style={{
                                display: 'flex',
                                gap: '8px',
                                padding: '8px',
                                borderBottom: '1px solid #e5e7eb',
                                alignItems: 'center'
                              }}
                            >
                              <div 
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  backgroundImage: `url(${getOptimizedImageUrl(artwork.cover_image, 'thumbnail')})`,
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  borderRadius: '4px',
                                  flexShrink: 0
                                }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ 
                                  fontSize: '11px', 
                                  fontWeight: '500',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {artwork.title}
                                </div>
                                <div style={{ fontSize: '10px', color: '#6b7280' }}>
                                  {artwork.width}"×{artwork.height}" • ${parseFloat(artwork.price).toLocaleString()}
                                </div>
                              </div>
                              <button
                                onClick={() => handleAddArtworkToCollection(artwork)}
                                disabled={alreadyAdded || addingArtworkId === artwork.vc_id}
                                style={{
                                  padding: '4px 8px',
                                  fontSize: '10px',
                                  backgroundColor: alreadyAdded ? '#9ca3af' : '#10b981',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: alreadyAdded ? 'not-allowed' : 'pointer',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {addingArtworkId === artwork.vc_id ? '...' : alreadyAdded ? 'Added' : '+ Add'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  {!artistSearchResults && (
                    <div style={{ 
                      padding: '16px', 
                      textAlign: 'center', 
                      color: '#6b7280',
                      fontSize: '12px'
                    }}>
                      <p style={{ marginBottom: '8px' }}>Search by first name, last name, email, nickname, or handle to find artists.</p>
                      <p style={{ fontSize: '10px', color: '#9ca3af' }}>
                        Try: emmett
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* Inventory Panel with Tabs for Accepted/Pending */
                <>
                  <div className="inventory-header flex-shrink-0" style={{ flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                      <h2 className="text-xs font-medium">Artwork Inventory</h2>
                      <span className="artwork-count">
                        {/* Show fallback count if no new inventory data */}
                        {(inventoryData.accepted.length === 0 && inventoryData.pending.length === 0 && sceneData.unplaced?.length > 0)
                          ? `${sceneData.unplaced.length} unplaced`
                          : inventoryTab === 'accepted' 
                            ? `${inventoryData.totalAccepted} accepted`
                            : `${inventoryData.totalPending} pending`
                        }
                      </span>
                    </div>
                    
                    {/* Tab buttons - only show if we have inventory data */}
                    {(inventoryData.accepted.length > 0 || inventoryData.pending.length > 0) && (
                      <div style={{ display: 'flex', gap: '4px', width: '100%' }}>
                        <button
                          onClick={() => { setInventoryTab('accepted'); setInventoryDisplayCount(30); }}
                          style={{
                            flex: 1,
                            padding: '6px 8px',
                            fontSize: '11px',
                            fontWeight: inventoryTab === 'accepted' ? '600' : '400',
                            backgroundColor: inventoryTab === 'accepted' ? '#10b981' : '#f3f4f6',
                            color: inventoryTab === 'accepted' ? 'white' : '#6b7280',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          ✓ Accepted ({inventoryData.totalAccepted})
                        </button>
                        <button
                          onClick={() => { setInventoryTab('pending'); setInventoryDisplayCount(30); }}
                          style={{
                            flex: 1,
                            padding: '6px 8px',
                            fontSize: '11px',
                            fontWeight: inventoryTab === 'pending' ? '600' : '400',
                            backgroundColor: inventoryTab === 'pending' ? '#f59e0b' : '#f3f4f6',
                            color: inventoryTab === 'pending' ? 'white' : '#6b7280',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          ? Pending ({inventoryData.totalPending})
                        </button>
                      </div>
                    )}
                    
                    {/* Search input */}
                    <input
                      type="text"
                      placeholder="Search artworks..."
                      value={inventorySearch}
                      onChange={(e) => { setInventorySearch(e.target.value); setInventoryDisplayCount(30); }}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        fontSize: '12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        outline: 'none'
                      }}
                    />
                  </div>
                  
                  <div 
                    id="inventory" 
                    ref={inventoryContainerRef}
                    className="bg-white p-4 rounded shadow flex-grow overflow-y-auto"
                    onScroll={handleInventoryScroll}
                  >
                    {(() => {
                      // Check if we have data from the new inventory system
                      const hasNewInventoryData = inventoryData.accepted.length > 0 || inventoryData.pending.length > 0;
                      
                      // If no new inventory data, fall back to sceneData.unplaced
                      if (!hasNewInventoryData && sceneData.unplaced && sceneData.unplaced.length > 0) {
                        // Use the original unplaced artworks
                        const filtered = sceneData.unplaced.filter(art => {
                          if (!inventorySearch) return true;
                          const search = inventorySearch.toLowerCase();
                          return (
                            art.title?.toLowerCase().includes(search) ||
                            String(art.vc_id).includes(search)
                          );
                        });
                        const displayed = filtered.slice(0, inventoryDisplayCount);
                        
                        if (filtered.length === 0) {
                          return (
                            <p className="text-gray-500 italic text-sm">
                              {inventorySearch ? 'No artworks match your search.' : 'No unplaced artworks available.'}
                            </p>
                          );
                        }
                        
                        return (
                          <>
                            {displayed.map(artwork => (
                              <InventoryItem
                                key={artwork.element_id}
                                artwork={artwork}
                                onDragStart={handleDragStart}
                                getOptimizedImageUrl={getOptimizedImageUrl}
                                audioMap={artworkAudioMap}
                              />
                            ))}
                            {filtered.length > displayed.length && (
                              <div style={{ 
                                padding: '12px', 
                                textAlign: 'center', 
                                color: '#6b7280',
                                fontSize: '11px'
                              }}>
                                Showing {displayed.length} of {filtered.length} • Scroll for more
                              </div>
                            )}
                          </>
                        );
                      }
                      
                      // Use the new inventory data with tabs
                      const currentList = inventoryTab === 'accepted' ? inventoryData.accepted : inventoryData.pending;
                      const filtered = currentList.filter(art => {
                        if (!inventorySearch) return true;
                        const search = inventorySearch.toLowerCase();
                        return (
                          art.title?.toLowerCase().includes(search) ||
                          art.artist_name?.toLowerCase().includes(search) ||
                          String(art.vc_id).includes(search)
                        );
                      });
                      const displayed = filtered.slice(0, inventoryDisplayCount);
                      
                      if (filtered.length === 0) {
                        return (
                          <p className="text-gray-500 italic text-sm">
                            {inventorySearch 
                              ? 'No artworks match your search.'
                              : inventoryTab === 'accepted'
                                ? 'No accepted artworks available.'
                                : 'No pending artworks available.'
                            }
                          </p>
                        );
                      }
                      
                      return (
                        <>
                          {displayed.map(artwork => (
                            <InventoryItem
                              key={artwork.vc_id}
                              artwork={{
                                ...artwork,
                                element_id: artwork.vc_id, // Use vc_id as element_id for drag
                                accepted: inventoryTab === 'accepted',
                                isFromInventory: true, // Flag to identify items from collection_vc_mapping
                                isInSceneElements: artwork.isInSceneElements || false
                              }}
                              onDragStart={handleDragStart}
                              getOptimizedImageUrl={getOptimizedImageUrl}
                              audioMap={artworkAudioMap}
                            />
                          ))}
                          {filtered.length > displayed.length && (
                            <div style={{ 
                              padding: '12px', 
                              textAlign: 'center', 
                              color: '#6b7280',
                              fontSize: '11px'
                            }}>
                              Showing {displayed.length} of {filtered.length} • Scroll for more
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      
      {/* Floating toggle button when sidebar is collapsed */}
      {isSidebarCollapsed && (
        <button 
          onClick={() => setIsSidebarCollapsed(false)}
          style={{
            position: 'fixed',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: '32px',
            height: '80px',
            backgroundColor: '#4f46e5',
            border: 'none',
            borderRadius: '8px 0 0 8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            boxShadow: '-2px 0 8px rgba(0,0,0,0.15)',
            zIndex: 100
          }}
          title="Show Artwork Panel"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      )}
      

      
      {/* Floor Information Display */}
      {floorData && (
        <div className="floor-info-panel">
          <h3>🏠 Gallery Floor</h3>
          <div className="floor-details">
            <div className="floor-property">
              <label>Dimensions:</label>
              <span>{floorData.width}" × {floorData.depth}" × {floorData.height}"</span>
            </div>
            <div className="floor-property">
              <label>Position:</label>
              <span>X: {floorData.position_x || 0}", Y: {floorData.position_y || 0}", Z: {floorData.position_z || 0}"</span>
            </div>
            <div className="floor-property">
              <label>Material:</label>
              <span>{floorData.material || 'wood'}</span>
            </div>
            {floorData.texture_url && (
              <div className="floor-property">
                <label>Texture:</label>
                <span>Custom texture loaded</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Collection Header */}
      <div className="collection-header">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {collectionInfo?.name || 'Collection'}
        </h1>
        <p className="text-gray-600 mb-6">
          {collectionInfo?.description || 'Gallery wall planning and management'}
        </p>
      </div>
      
      {/* Wall Modal */}
      {console.log('🔧 Modal state:', { showWallModal, editingWallId })}
      <WallModal
        isOpen={showWallModal}
        onClose={() => {
          console.log('🔧 Closing modal');
          setShowWallModal(false);
          setEditingWallId(null);
        }}
        onSubmit={handleWallSubmit}
        wall={getWallForEditing()}
      />
      
      {/* Copy Walls Modal */}
      {showCopyWallsModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => setShowCopyWallsModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600' }}>
              Copy Walls from Previous Collection
            </h2>
            <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
              {galleryInfo ? (
                <>Using gallery: <strong>{galleryInfo.name}</strong></>
              ) : (
                'Select a previous collection to copy its wall layout'
              )}
            </p>
            
            {previousCollections.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 16px', color: '#6b7280' }}>
                <p>No previous collections with walls found for this gallery.</p>
                <p style={{ fontSize: '13px', marginTop: '8px' }}>
                  Create walls manually using "Add Wall" button.
                </p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {previousCollections.map((collection) => (
                  <button
                    key={collection.collection_id}
                    onClick={() => handleCopyWalls(collection.collection_id)}
                    disabled={isCopyingWalls}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      cursor: isCopyingWalls ? 'wait' : 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = '#7c3aed';
                      e.currentTarget.style.backgroundColor = '#f5f3ff';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                        {collection.title}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        Collection #{collection.collection_id} • {new Date(collection.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{
                      backgroundColor: '#7c3aed',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      {collection.wall_count} walls
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', gap: '8px' }}>
              <button
                onClick={() => setShowCopyWallsModal(false)}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Dimension Edit Modal */}
      {showDimensionModal && editingArtworkDimensions && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowDimensionModal(false);
            setEditingArtworkDimensions(null);
          }}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '400px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600' }}>
              Edit Artwork Dimensions
            </h2>
            
            {/* Artwork Thumbnail */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              marginBottom: '16px',
              backgroundColor: '#f3f4f6',
              borderRadius: '8px',
              padding: '16px',
              minHeight: '120px'
            }}>
              <img 
                src={getOptimizedImageUrl(editingArtworkDimensions.source, 'display')}
                alt={editingArtworkDimensions.title || 'Artwork'}
                style={{ 
                  maxWidth: '280px', 
                  maxHeight: '200px', 
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }}
              />
            </div>
            
            {/* Artwork Info */}
            {editingArtworkDimensions.title && (
              <p style={{ margin: '0 0 8px 0', fontWeight: '500', textAlign: 'center' }}>
                {editingArtworkDimensions.title}
              </p>
            )}
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
              VC ID: {editingArtworkDimensions.vc_id}
            </p>
            
            {/* Dimension Inputs */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Width (inches)
                </label>
                <input
                  type="number"
                  value={dimensionFormData.width}
                  onChange={(e) => setDimensionFormData(prev => ({ ...prev, width: e.target.value }))}
                  step="0.25"
                  min="1"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., 24"
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  Height (inches)
                </label>
                <input
                  type="number"
                  value={dimensionFormData.height}
                  onChange={(e) => setDimensionFormData(prev => ({ ...prev, height: e.target.value }))}
                  step="0.25"
                  min="1"
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="e.g., 36"
                />
              </div>
            </div>
            
            {/* Hanger Height Input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                🪝 Hanger Height from Top (inches) <span style={{ fontWeight: '400', color: '#9ca3af' }}>- optional</span>
              </label>
              <input
                type="number"
                value={dimensionFormData.hanger_height}
                onChange={(e) => setDimensionFormData(prev => ({ ...prev, hanger_height: e.target.value }))}
                step="0.25"
                min="0.5"
                max={dimensionFormData.height || 100}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '16px',
                  boxSizing: 'border-box'
                }}
                placeholder="e.g., 3 (distance from top to wire/hanger)"
              />
              <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                Measure from the top of the artwork to where the hanging wire or hook sits. This helps calculate exact hook placement on the wall.
              </p>
            </div>
            
            {/* Current vs New preview */}
            <div style={{ 
              backgroundColor: '#f0fdf4', 
              border: '1px solid #bbf7d0', 
              borderRadius: '8px', 
              padding: '12px', 
              marginBottom: '20px',
              fontSize: '13px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#6b7280' }}>Current:</span>
                <span style={{ fontWeight: '500' }}>{editingArtworkDimensions.width}" × {editingArtworkDimensions.height}"</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>New:</span>
                <span style={{ fontWeight: '600', color: '#059669' }}>
                  {dimensionFormData.width || '—'}" × {dimensionFormData.height || '—'}"
                </span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button
                onClick={() => {
                  setShowDimensionModal(false);
                  setEditingArtworkDimensions(null);
                }}
                disabled={isSavingDimensions}
                style={{
                  padding: '10px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDimensions}
                disabled={isSavingDimensions || !dimensionFormData.width || !dimensionFormData.height}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: isSavingDimensions ? '#9ca3af' : '#3b82f6',
                  color: 'white',
                  cursor: isSavingDimensions ? 'wait' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {isSavingDimensions ? 'Saving...' : 'Save Dimensions'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Measurements Modal - Shows placement info + editable hanger height */}
      {showMeasurementsModal && measurementsArtwork && (() => {
        const { artwork, wallWidth, wallHeight, wallArtworks = [] } = measurementsArtwork;
        const posX = artwork.wall_position_x !== undefined ? artwork.wall_position_x : 
                    (artwork.position_x !== undefined ? artwork.position_x : 0);
        const posY = artwork.wall_position_y !== undefined ? artwork.wall_position_y : 
                    (artwork.position_y !== undefined ? artwork.position_y : 0);
        const bottomFromTop = posY + artwork.height;
        const heightFromFloor = wallHeight - bottomFromTop;
        const centerFromFloor = wallHeight - posY - (artwork.height / 2);
        
        // Calculate center of artwork on wall
        const artworkCenterX = posX + (artwork.width / 2);
        const wallCenterX = wallWidth / 2;
        const distanceFromLeftEdge = artworkCenterX;
        const distanceFromRightEdge = wallWidth - artworkCenterX;
        
        // Calculate distance to nearest adjacent artwork
        const artworkLeftEdge = posX;
        const artworkRightEdge = posX + artwork.width;
        let nearestArtworkLeft = null; // Distance from this artwork's left edge to nearest artwork on left
        let nearestArtworkRight = null; // Distance from this artwork's right edge to nearest artwork on right
        
        wallArtworks.forEach(other => {
          if (other.element_id === artwork.element_id) return; // Skip self
          const otherPosX = other.wall_position_x !== undefined ? other.wall_position_x : 
                           (other.position_x !== undefined ? other.position_x : 0);
          const otherLeftEdge = otherPosX;
          const otherRightEdge = otherPosX + other.width;
          
          // Check if other artwork is to the left (its right edge is to the left of our left edge)
          if (otherRightEdge <= artworkLeftEdge) {
            const gap = artworkLeftEdge - otherRightEdge;
            if (nearestArtworkLeft === null || gap < nearestArtworkLeft) {
              nearestArtworkLeft = gap;
            }
          }
          
          // Check if other artwork is to the right (its left edge is to the right of our right edge)
          if (otherLeftEdge >= artworkRightEdge) {
            const gap = otherLeftEdge - artworkRightEdge;
            if (nearestArtworkRight === null || gap < nearestArtworkRight) {
              nearestArtworkRight = gap;
            }
          }
        });
        
        // Distance from hook center to nearest artwork edge
        const hookFromNearestArtworkLeft = nearestArtworkLeft !== null ? nearestArtworkLeft + (artwork.width / 2) : null;
        const hookFromNearestArtworkRight = nearestArtworkRight !== null ? nearestArtworkRight + (artwork.width / 2) : null;
        
        // Get hanger height for hook calculation
        const currentHangerHeight = measurementsHangerHeight ? parseFloat(measurementsHangerHeight) : null;
        const hookPlacement = currentHangerHeight ? heightFromFloor + artwork.height - currentHangerHeight : null;
        
        return (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
            onClick={() => {
              setShowMeasurementsModal(false);
              setMeasurementsArtwork(null);
            }}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                maxWidth: '420px',
                width: '90%'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📏 Placement Measurements
              </h2>
              
              {/* Artwork Info */}
              {artwork.title && (
                <p style={{ margin: '0 0 4px 0', fontWeight: '500' }}>
                  {artwork.title}
                </p>
              )}
              <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280' }}>
                {artwork.width}" × {artwork.height}" | VC ID: {artwork.vc_id}
              </p>
              
              {/* Measurements Grid */}
              <div style={{ 
                backgroundColor: '#f3f4f6', 
                borderRadius: '8px', 
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Bottom from Floor</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#374151' }}>{formatDimension(heightFromFloor)}</div>
                    <div style={{ fontSize: '10px', color: '#9ca3af' }}>{heightFromFloor.toFixed(1)}"</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Center from Floor</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#374151' }}>{formatDimension(centerFromFloor)}</div>
                    <div style={{ fontSize: '10px', color: '#9ca3af' }}>{centerFromFloor.toFixed(1)}"</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>From Left Edge</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#374151' }}>{formatDimension(distanceFromLeftEdge)}</div>
                    <div style={{ fontSize: '10px', color: '#9ca3af' }}>{distanceFromLeftEdge.toFixed(1)}"</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '8px', backgroundColor: 'white', borderRadius: '6px' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>From Right Edge</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: '#374151' }}>{formatDimension(distanceFromRightEdge)}</div>
                    <div style={{ fontSize: '10px', color: '#9ca3af' }}>{distanceFromRightEdge.toFixed(1)}"</div>
                  </div>
                </div>
                
                {/* Hook Placement - highlighted */}
                {hookPlacement !== null && (
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '12px', 
                    backgroundColor: '#d1fae5', 
                    borderRadius: '6px',
                    border: '1px solid #10b981'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '11px', color: '#065f46', marginBottom: '4px' }}>🪝 Hook Placement from Floor</div>
                      <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>{formatDimension(hookPlacement)}</div>
                      <div style={{ fontSize: '11px', color: '#065f46', marginBottom: '8px' }}>{hookPlacement.toFixed(2)}"</div>
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '8px', 
                        marginTop: '8px',
                        paddingTop: '8px',
                        borderTop: '1px dashed #10b981'
                      }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '9px', color: '#065f46' }}>Hook from<br/>artwork edge</div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>{formatDimension(artwork.width / 2)}</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '9px', color: '#065f46' }}>Artwork from<br/>nearest wall</div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>{formatDimension(Math.min(posX, wallWidth - posX - artwork.width))}</div>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '9px', color: '#065f46' }}>Hook from<br/>nearest wall</div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>{formatDimension(Math.min(distanceFromLeftEdge, distanceFromRightEdge))}</div>
                        </div>
                      </div>
                      
                      {/* Distance to adjacent artwork */}
                      {(hookFromNearestArtworkLeft !== null || hookFromNearestArtworkRight !== null) && (
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: hookFromNearestArtworkLeft !== null && hookFromNearestArtworkRight !== null ? '1fr 1fr' : '1fr',
                          gap: '8px', 
                          marginTop: '8px',
                          paddingTop: '8px',
                          borderTop: '1px dashed #10b981'
                        }}>
                          {hookFromNearestArtworkLeft !== null && (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '9px', color: '#065f46' }}>Hook from left<br/>artwork edge</div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>{formatDimension(hookFromNearestArtworkLeft)}</div>
                              <div style={{ fontSize: '8px', color: '#6b7280' }}>gap: {formatDimension(nearestArtworkLeft)}</div>
                            </div>
                          )}
                          {hookFromNearestArtworkRight !== null && (
                            <div style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: '9px', color: '#065f46' }}>Hook from right<br/>artwork edge</div>
                              <div style={{ fontSize: '13px', fontWeight: '600', color: '#059669' }}>{formatDimension(hookFromNearestArtworkRight)}</div>
                              <div style={{ fontSize: '8px', color: '#6b7280' }}>gap: {formatDimension(nearestArtworkRight)}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {!hookPlacement && (
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '12px', 
                    backgroundColor: '#fef3c7', 
                    borderRadius: '6px',
                    textAlign: 'center',
                    fontSize: '12px',
                    color: '#92400e'
                  }}>
                    ↓ Enter hanger height below to calculate hook placement
                  </div>
                )}
              </div>
              
              {/* Hanger Height Input */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: '#374151' }}>
                  🪝 Hanger Height from Top (inches)
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="number"
                    value={measurementsHangerHeight}
                    onChange={(e) => setMeasurementsHangerHeight(e.target.value)}
                    step="0.25"
                    min="0.5"
                    max={artwork.height}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="e.g., 3"
                  />
                  <button
                    onClick={handleSaveMeasurementsHangerHeight}
                    disabled={isSavingMeasurements}
                    style={{
                      padding: '10px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: isSavingMeasurements ? '#9ca3af' : '#10b981',
                      color: 'white',
                      cursor: isSavingMeasurements ? 'wait' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isSavingMeasurements ? 'Saving...' : 'Save'}
                  </button>
                </div>
                <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px', marginBottom: 0 }}>
                  Distance from top of frame to hanging wire/hook. Saves immediately.
                </p>
              </div>
              
              {/* Close Button */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowMeasurementsModal(false);
                    setMeasurementsArtwork(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      
      {/* Top-Down Wall Planner */}
      {showTopDownPlanner && (
        <TopDownWallPlanner
          walls={sceneData.walls}
          onUpdateWall={handleUpdateWallPosition}
          onClose={() => setShowTopDownPlanner(false)}
          onAddWall={handleWallSubmit}
          floorData={floorData}
          onAddPod={handlePodSubmit}
          onAddEasel={handleEaselSubmit}
          easels={sceneData.easels || []}
          onUpdateEasel={handleUpdateEaselPosition}
          structures={sceneData.structures || []}
          onAddStructure={handleStructureSubmit}
          onUpdateStructure={handleUpdateStructurePosition}
          // Art Placement Mode props
          sceneData={sceneData}
          setSceneData={setSceneData}
          inventoryData={inventoryData}
          collectionId={collectionId}
          onPlaceArtwork={handleDrop}
          onPlaceArtworkOnEasel={handleEaselDrop}
          onRepositionArtwork={handleArtworkReposition}
          onRemoveArtwork={removeArtwork}
          onRefreshData={async () => {
            const responseData = await fetchSceneElements(collectionId, '2d');
            setSceneData(responseData);
            loadInventoryData();
          }}
          getOptimizedImageUrl={getOptimizedImageUrl}
          toolsActions={{
            onViewIn3D: viewIn3D,
            onGenerateLabels: goToPdfGenerator,
            onPrintWallLayouts: printWallLayouts,
            onPrintEaselLayouts: printEaselLayouts,
            onCopyWallsFromPrevious: loadPreviousCollections,
            onUpdateMetadata: handleUpdateMetadata,
            onRefresh: refreshData
          }}
          onDeleteWall={handleDeleteWall}
          onDeleteEasel={handleDeleteEasel}
          onDeleteStructure={handleDeleteStructure}
          onDeletePod={handleDeletePod}
        />
      )}
    </div>
  );
};

export default GalleryWallPlanner;